/**
 * AgentConfigEditor - Master-Detail form for editing AI agent configurations
 * Reads from and writes to the top-level agents map (Single Source of Truth)
 */
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Tab } from '@headlessui/react';
import SupervisorMemberPicker from './SupervisorMemberPicker';
import TagInput from './TagInput.jsx';
import AgentToolPicker from '@stepflow/ui-shared/components/agentTools/AgentToolPicker.jsx';
import { listTools, listMcpRegistry, listMcpCredentials, listWorkspaces, listSecretsRegistry } from '@stepflow/ui-shared/services/toolsApi';
import { UB_APPROVALS_ENABLED } from '@stepflow/ui-shared/constants/universalBuilderFlags.js';
import { useToast } from '@stepflow/ui-shell/context/ToastContext.jsx';
import FieldLockBadge from './FieldLockBadge';
import GraphVisualization from './GraphVisualization';
import ManifestTreeFlow from './ManifestTreeFlow';

const TOOLS_ENV = (import.meta.env.VITE_TOOLS_ENV || import.meta.env.VITE_ENV || 'dev')
  .toString()
  .trim();

// How often we refresh the soft lock while the user is still typing. The
// server-side TTL is 120s by default; we renew at ~50% so a brief network
// blip doesn't leak the lock to a peer.
const LOCK_REFRESH_INTERVAL_MS = 60_000;

// Runtime agent tiers → `/agent/config/llm/{tier}` (fast=Haiku, think=Sonnet, reason=Opus).
const MODEL_OPTIONS = ['fast', 'think', 'reason'];
// Friendly tier labels (values stay 'fast'/'think'/'reason' on the backend).
const MODEL_LABELS = {
  fast: 'Fast — quick & economical',
  think: 'Balanced — for multi-step work',
  reason: 'Deep — for complex reasoning',
};
// Generative-engine tiers resolve through the AGENT SSM path (fast=Haiku, think=Sonnet,
// reason=Opus) — same family naming the Pricing page uses. Surfaced so the Lead/Judge/
// Final selects aren't a blackbox; the engine's per-role DEFAULT tier is set at each row.
const ENGINE_TIER_MODEL = { fast: 'Haiku', think: 'Sonnet', reason: 'Opus' };
// Genericize the architect's model-name suggestion (e.g. "Sonnet recommended") to the tier's
// GENERIC label — we never surface a raw model name (the underlying model can change). Maps the
// model family in the recommendation → the short tier word shown in the "Thinking power" select.
const MODEL_FAMILY_TO_TIER_WORD = { haiku: 'Fast', sonnet: 'Balanced', opus: 'Deep' };
function genericModelSuggestion(rawRecommendation) {
  const s = String(rawRecommendation || '').toLowerCase();
  for (const [family, word] of Object.entries(MODEL_FAMILY_TO_TIER_WORD)) {
    if (s.includes(family)) return word;
  }
  return '';
}
// "Acting on its own" (``permissions.email_mode``) — ONE policy for the agent's emails AND its room answers
// (STAND-IN.md §9; the email path ``worker/agent.py`` reads the same values): AUTONOMOUS acts directly · SUPERVISED
// goes to the Approver only below "Confidence needed to act" · DRAFT_ONLY always waits for the Approver. Unset = the
// backend's DRAFT_ONLY default, so the form shows that (it used to show AUTONOMOUS for an unset agent).
const EMAIL_MODE_OPTIONS = ['AUTONOMOUS', 'SUPERVISED', 'DRAFT_ONLY'];
const EMAIL_MODE_LABELS = {
  AUTONOMOUS: 'On its own — send and answer directly',
  SUPERVISED: 'Ask the Approver when unsure',
  DRAFT_ONLY: 'Always draft for the Approver',
};
const EMAIL_MODE_UNSET = 'DRAFT_ONLY';
const CONFIDENCE_UNSET = 0.7;   // what the runtime uses when an agent has no threshold saved

/** Bedrock Knowledge Base (`knowledgeBaseId` / engine.retrieve) — not in use; show when we adopt KB. */
const SHOW_BEDROCK_KNOWLEDGE_BASE_UI = false;

/** Dynamo adjacency uses AGENT#Shared for network-wide Shared/ pool docs — not a deployable agent. */
const ROSTER_EXCLUDED_AGENT_IDS = new Set(['Shared']);

/**
 * Reserved agents-map key holding the network's ROOM CONTEXT injection setting
 * (NOT a runnable agent). Stored in the agents map so it versions with the
 * snapshot for free; excluded everywhere agents are iterated as runnable. It
 * DOES appear in the Select Agent dropdown as a special "Room context" entry
 * whose panel edits ``{ injection: 'off' | 'room' }``.
 * ON by default ('room') — rooms get better results building on prior context;
 * only an EXPLICIT 'off' opts out (mirrors backend _network_room_context_mode).
 * (Network-wide consolidation is a SEPARATE on-demand "Extract network context"
 * action in the network's ⋯ menu — not an injection mode.)
 */
const ROOM_CONTEXT_AGENT_KEY = '__roomContext__';
const ROOM_CONTEXT_INJECTION_OPTIONS = [
  { value: 'room', label: 'On', desc: "After each successful run, this room's overall context is recompiled (async) and injected — read-only — into the next run, so agents build on it. (Default.)" },
  { value: 'off', label: 'Off', desc: 'Runs stay independent — no room context is compiled or injected. You can still compile it manually in View Context.' },
];
// Roster exclusion includes the reserved room-context key (never deployed/run).
ROSTER_EXCLUDED_AGENT_IDS.add(ROOM_CONTEXT_AGENT_KEY);

function deepEqual(a, b) {
  if (a === b) return true;
  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false;
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) return false;
  return keysA.every((k) => keysB.includes(k) && deepEqual(a[k], b[k]));
}

// Default HitL SLA for a brand-new agent. 30 minutes balances "someone is
// probably at their desk" for routine approvals with "not so long the task
// token expires" (SFN callback tokens live 1 year, but our lease TTL is
// much shorter — see ``approvalTimeoutMinutes`` below).
const DEFAULT_APPROVAL_TIMEOUT_MINUTES = 30;

// Hard ceiling mirrors the DynamoDB TTL caps in ``storage.py``; 24h is
// already the ESCALATED window, longer makes the auto-escalation meaningless.
const MAX_APPROVAL_TIMEOUT_MINUTES = 24 * 60;

// ── Builder run-safety caps (UNIVERSAL_BUILDER only) ────────────────────────
// Per-agent ceilings hydrated + enforced by the SDK at run time
// (ClaudeAgentOptions.max_budget_usd / task_budget / max_turns). A safety net
// against a runaway claude-code burning tokens unbounded. Frozen at release
// like other agent config; rooms cannot override (safety). Stored as canonical
// units: USD float, raw token int, turn int.
const SAFETY_CAP_DEFAULTS = Object.freeze({
  maxBudgetUsd: 2.0,      // $ per run
  maxTokens: 1_000_000,   // total tokens per run
  maxTurns: 60,           // assistant turns per run
});
const SAFETY_CAP_MAX = Object.freeze({
  maxBudgetUsd: 100.0,
  maxTokens: 50_000_000,
  maxTurns: 1000,
});
// Clamp a value to [1-ish, max]; fall back to default on non-finite/≤0.
function _clampCap(v, def, max, { float = false } = {}) {
  const n = Number(v);
  if (!Number.isFinite(n) || n <= 0) return def;
  const c = Math.min(n, max);
  return float ? Math.round(c * 100) / 100 : Math.round(c);
}

// The six granular memory subsystems, grouped into three friendly segments
// for the Workbench UI. The backend keys (`key`) are the single source of
// truth — they mirror `stepflow_core/memory_flags.py::MEMORY_FLAG_KEYS` and
// MUST NOT change. Only the user-facing `label`/`desc`/`group` are friendly.
//   group 'context' — rule-based context injected every turn (org + room)
//   group 'longterm' — searchable long-term memory that grows over time
//   group 'buffer'   — short-term conversation history (email threads)
const MEMORY_FLAG_DEFS = [
  // — Context (rule-based) —
  // Organization context is ALWAYS ON by design — it's static org knowledge
  // injected directly. The only way to remove it is to delete the file from
  // the Data Plane, not a per-agent toggle. So it's never rendered and always
  // persisted `true` (see ALWAYS_ON_MEMORY_FLAG_KEYS / setMaster).
  { key: 'globalContext', group: 'context', label: 'Organization context', desc: "Always include your organization's shared knowledge.", alwaysOn: true },
  { key: 'poolMemory', group: 'context', label: 'Room memory', desc: 'Carry forward what happened in earlier runs in this room.' },
  // — Long-term memory (was "Vector") —
  // `vectorRecall` gates BOTH jobs of the vector store: (a) conversational
  // recall of what the agent learned before, AND (b) looking up REFERENCE
  // documents uploaded to the Data Plane (RULES docs are always-injected and
  // ungated — they ride the "Organization context" always-on path instead).
  // One switch, because both jobs share the same backend gate + store —
  // splitting them in the UI would falsely imply independent control.
  { key: 'vectorRecall', group: 'longterm', label: 'Remember & recall', desc: 'Reuse what the agent learned before, and look things up in reference documents you upload.' },
  // Auto-save only WRITES to long-term memory; without "Remember & recall"
  // the agent can never read it back. So it's a DEPENDENT sub-option:
  // disabled + forced off when vectorRecall is off.
  { key: 'autoVectorize', group: 'longterm', label: 'Auto-save documents', desc: 'Automatically save uploaded documents to long-term memory for later recall.', dependsOn: 'vectorRecall' },
  // Knowledge Base (Bedrock RAG) is not in use yet. Gate its toggle on the
  // same flag that hides the Knowledge Base ID field (SHOW_BEDROCK_KNOWLEDGE_BASE_UI)
  // so flipping that one constant re-enables both when we adopt KB. While
  // hidden, the flag is forced OFF on write (see setMaster) and the engine
  // is inert anyway (RAG gates on knowledgeBase AND a configured kb_id).
  { key: 'knowledgeBase', group: 'longterm', label: 'Knowledge base', desc: 'Look up answers from the connected knowledge base.', hidden: !SHOW_BEDROCK_KNOWLEDGE_BASE_UI },
  // — Conversation buffer —
  { key: 'sessionBuffer', group: 'buffer', label: 'Email conversation memory', desc: 'Remember earlier messages in the same email thread.' },
];
// Friendly segment headers + render order. Each group renders its visible
// flags under a small heading.
const MEMORY_GROUPS = [
  { id: 'context', label: 'Context', hint: "Background the agent always has on hand. Your organization's shared knowledge is always included." },
  { id: 'longterm', label: 'Long-term memory', hint: 'Knowledge the agent recalls across runs, including reference documents you upload.' },
  { id: 'buffer', label: 'Conversation', hint: 'Short-term memory within an ongoing email thread.' },
];
const MEMORY_FLAG_KEYS = MEMORY_FLAG_DEFS.map((d) => d.key);
// Subset shown in the UI and driven by the master toggle. Two kinds of
// non-toggleable flags are excluded from the visible set:
//   • hidden  (e.g. knowledgeBase while KB is unused) — persisted OFF
//   • alwaysOn (e.g. globalContext — org context is by-design always on,
//     removable only by deleting the Data Plane file) — persisted ON
const VISIBLE_MEMORY_FLAG_DEFS = MEMORY_FLAG_DEFS.filter((d) => !d.hidden && !d.alwaysOn);
const VISIBLE_MEMORY_FLAG_KEYS = VISIBLE_MEMORY_FLAG_DEFS.map((d) => d.key);
const HIDDEN_MEMORY_FLAG_KEYS = MEMORY_FLAG_DEFS.filter((d) => d.hidden).map((d) => d.key);
const ALWAYS_ON_MEMORY_FLAG_KEYS = MEMORY_FLAG_DEFS.filter((d) => d.alwaysOn).map((d) => d.key);

// Coerce a stored value to a strict boolean, matching backend `_coerce_bool`.
function coerceMemBool(v, fallback) {
  if (typeof v === 'boolean') return v;
  if (typeof v === 'number') return v !== 0;
  if (typeof v === 'string') {
    const s = v.trim().toLowerCase();
    if (['true', '1', 'yes', 'on'].includes(s)) return true;
    if (['false', '0', 'no', 'off'].includes(s)) return false;
  }
  return fallback;
}

// Resolve the effective 6 flags from formData, mirroring the backend
// `resolve_memory_flags`: legacy `memoryEnabled` is the per-flag fallback;
// nested `memory.{flag}` wins when present; absent → true.
function resolveMemoryFlags(formData) {
  const legacy = formData && 'memoryEnabled' in formData
    ? coerceMemBool(formData.memoryEnabled, true)
    : true;
  const nested = formData && formData.memory && typeof formData.memory === 'object'
    ? formData.memory
    : {};
  const out = {};
  for (const k of MEMORY_FLAG_KEYS) {
    out[k] = k in nested ? coerceMemBool(nested[k], legacy) : legacy;
  }
  return out;
}

function defaultAgent() {
  return {
    logicalId: '',
    type: 'AI',
    modelId: 'think',
    memoryEnabled: true,
    bufferSize: 5,
    maxToolIterations: 12,
    // Hard cap on recall-memory invocations per LIGHT-mode execution.
    // Prevents ReAct loops where the agent re-queries memory endlessly
    // instead of answering with what it already has. 3 is the sweet
    // spot for legitimate multi-hop ("policy → exception → precedent")
    // without giving the model a license to spin.
    memoryRecallMaxIterations: 3,
    maxSummaryLength: 50000,
    knowledgeBaseId: '',
    systemPrompt: '',
    level: 2,
    reports_to: '',
    // HitL SLA: auto-escalate any approval this agent raises after this
    // many minutes without resolution. Per-agent so a "Legal Signoff"
    // agent can budget 4h while a "Shipping Confirmation" agent uses 5m.
    approvalTimeoutMinutes: DEFAULT_APPROVAL_TIMEOUT_MINUTES,
    /** Universal Builder (CodeBuild PTY) default when launcher omits executionMode. */
    builderExecutionMode: 'background',
    identity: { email_prefix: '', signature: '' },
    permissions: {
      email_mode: 'AUTONOMOUS',
      confidence_threshold: 0.95,
      max_sync_textract_size_mb: 5,
      supervisor_email: '',
      allowed_consultants: ['*'],
      allowed_emails: ['*']
    },
    session_config: { key_field: 'sender' },
    tools: [],
    // MCP servers this agent is allowed to load (Universal Builder). Always
    // an array so the toggles bind cleanly and the field persists on save.
    enabledMcpIds: [],
    // Build-time DEFAULT shared workspace (one per agent). '' = none. A room
    // override (setAgentWorkspaceOverride) beats this at run time; this is the
    // network-level default, frozen into the release. See doc/arch/TEMPLATES.md.
    defaultWorkspaceId: '',
    // Builder run-safety caps (enforced by the SDK at run time). Defaults are
    // generous-but-bounded; frozen at release; rooms can't override.
    maxBudgetUsd: SAFETY_CAP_DEFAULTS.maxBudgetUsd,
    maxTokens: SAFETY_CAP_DEFAULTS.maxTokens,
    maxTurns: SAFETY_CAP_DEFAULTS.maxTurns,
  };
}

function mergeWithDefaults(config) {
  const d = defaultAgent();
  const merged = {
    ...d,
    ...config,
    identity: { ...d.identity, ...(config?.identity || {}) },
    permissions: { ...d.permissions, ...(config?.permissions || {}) },
    session_config: { ...d.session_config, ...(config?.session_config || {}) }
  };
  /** Only real arrays persist as tools — never null/non-array from API/Dynamo (would wipe chips on save). */
  merged.tools = Array.isArray(config?.tools) ? config.tools : [...d.tools];
  // Back-fill agents created before the HitL SLA feature existed. We clamp
  // rather than reject so a corrupt value (0, negative, huge) still yields
  // a usable defaulted config on the wire.
  const raw = Number(merged.approvalTimeoutMinutes);
  merged.approvalTimeoutMinutes = Number.isFinite(raw) && raw > 0
    ? Math.min(Math.max(Math.round(raw), 1), MAX_APPROVAL_TIMEOUT_MINUTES)
    : DEFAULT_APPROVAL_TIMEOUT_MINUTES;
  const bem = merged.builderExecutionMode;
  merged.builderExecutionMode =
    bem === 'interactive' || bem === 'background'
      ? bem
      : 'background';
  // Builder run-safety caps — clamp to sane bounds (back-fills pre-feature
  // agents to defaults; corrupt/huge values are clamped, never rejected).
  merged.maxBudgetUsd = _clampCap(merged.maxBudgetUsd, SAFETY_CAP_DEFAULTS.maxBudgetUsd, SAFETY_CAP_MAX.maxBudgetUsd, { float: true });
  merged.maxTokens = _clampCap(merged.maxTokens, SAFETY_CAP_DEFAULTS.maxTokens, SAFETY_CAP_MAX.maxTokens);
  merged.maxTurns = _clampCap(merged.maxTurns, SAFETY_CAP_DEFAULTS.maxTurns, SAFETY_CAP_MAX.maxTurns);
  // AVAILABILITY (ROOM-SCOPES) — per-agent room availability, frozen into the release footprint
  // like every other field. '' = enabled (default); 'branch' = disable agent + its whole team;
  // 'self' = bypass just this agent's turn (team still runs). Coerce anything else to '' so a
  // garbage value can't reach the frozen snapshot or falsely mark the form dirty.
  const dm = String(merged.disabledMode || '').trim().toLowerCase();
  merged.disabledMode = (dm === 'branch' || dm === 'self') ? dm : '';
  return merged;
}


/**
 * Approval-timeout input — value + unit (minutes/hours) pair. We persist
 * a single integer in minutes (easiest to reason about on the backend
 * lease clock) but let the user think in whichever unit is natural.
 *
 * Choosing "hours" round-trips the value through a ``* 60`` / ``/ 60``
 * conversion; we snap to whole hours when possible so "2 hours" doesn't
 * silently become "2h 3m" after a save/reload.
 */
function ApprovalTimeoutInput({ minutes, onChange }) {
  const safeMinutes = Number.isFinite(minutes) && minutes > 0
    ? Math.min(Math.round(minutes), MAX_APPROVAL_TIMEOUT_MINUTES)
    : DEFAULT_APPROVAL_TIMEOUT_MINUTES;
  // A timeout is "naturally expressed in hours" when it divides evenly
  // *and* is >= 1h; everything else stays in minutes.
  const preferHours = safeMinutes >= 60 && safeMinutes % 60 === 0;
  const [unit, setUnit] = React.useState(preferHours ? 'hours' : 'minutes');

  const displayValue = unit === 'hours'
    ? Math.max(1, Math.round(safeMinutes / 60))
    : safeMinutes;
  const maxForUnit = unit === 'hours'
    ? Math.floor(MAX_APPROVAL_TIMEOUT_MINUTES / 60)
    : MAX_APPROVAL_TIMEOUT_MINUTES;

  const handleValueChange = (e) => {
    const raw = parseInt(e.target.value, 10);
    if (!Number.isFinite(raw) || raw <= 0) {
      // Let the user clear the field while editing; only clamp on blur
      // so the cursor doesn't jump around during typing.
      onChange(DEFAULT_APPROVAL_TIMEOUT_MINUTES);
      return;
    }
    const clamped = Math.min(raw, maxForUnit);
    const asMinutes = unit === 'hours' ? clamped * 60 : clamped;
    onChange(asMinutes);
  };

  const handleUnitChange = (e) => {
    // Switching units only affects display, never the underlying value —
    // minutes=180 shown as "3 hours" vs "180 minutes" is the same policy.
    setUnit(e.target.value);
  };

  return (
    <div className="flex items-center gap-2">
      <input
        type="number"
        min={1}
        max={maxForUnit}
        value={displayValue}
        onChange={handleValueChange}
        className="block w-24 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
      />
      <select
        value={unit}
        onChange={handleUnitChange}
        className="rounded-lg border border-gray-300 bg-white px-2 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
      >
        <option value="minutes">minutes</option>
        <option value="hours">hours</option>
      </select>
    </div>
  );
}

/** Token input with a K/M unit dropdown. User types a small number + picks
 *  unit; we store the raw integer. 1 × M = 1,000,000. Errors are impossible —
 *  no free-text units to mistype; clamped to the cap max. */
function TokenCapInput({ tokens, onChange }) {
  const safe = _clampCap(tokens, SAFETY_CAP_DEFAULTS.maxTokens, SAFETY_CAP_MAX.maxTokens);
  // Choose the natural unit for display: M if ≥1M and clean-ish, else K.
  const preferM = safe >= 1_000_000;
  const [unit, setUnit] = React.useState(preferM ? 'M' : 'K');
  const factor = unit === 'M' ? 1_000_000 : 1_000;
  const displayValue = Math.round((safe / factor) * 100) / 100;
  const maxForUnit = Math.floor(SAFETY_CAP_MAX.maxTokens / factor);

  return (
    // Single bordered box: number input + an in-field unit selector on the
    // right (borderless, compact). focus-within mirrors the input's focus ring.
    <div className="flex w-32 items-center rounded-lg border border-gray-300 bg-white pr-1 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500">
      <input
        type="number" min={1} max={maxForUnit} step={unit === 'M' ? 0.5 : 50}
        value={displayValue}
        onChange={(e) => {
          const raw = parseFloat(e.target.value);
          if (!Number.isFinite(raw) || raw <= 0) { onChange(SAFETY_CAP_DEFAULTS.maxTokens); return; }
          onChange(_clampCap(raw * factor, SAFETY_CAP_DEFAULTS.maxTokens, SAFETY_CAP_MAX.maxTokens));
        }}
        className="w-full min-w-0 rounded-l-lg border-0 bg-transparent px-3 py-2 text-sm focus:outline-none focus:ring-0"
      />
      <select
        value={unit}
        onChange={(e) => setUnit(e.target.value)}  /* display-only; value stays raw */
        aria-label="Token unit"
        className="shrink-0 cursor-pointer border-0 bg-transparent py-2 pl-1 pr-1 text-xs text-gray-600 focus:outline-none focus:ring-0"
      >
        <option value="K">K</option>
        <option value="M">M</option>
      </select>
    </div>
  );
}

/** The 3 builder run-safety caps in a single row: $ budget · tokens (K/M) ·
 *  turns. Number-only inputs, unit affordances, clamped — user just types. */
function SafetyCapsRow({ formData, update }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        Run safety caps
        <span className="ml-2 text-xs font-normal text-gray-500">
          hard ceilings per builder run — the run stops if any is hit (rooms can't override)
        </span>
      </label>
      <div className="flex flex-wrap items-end gap-4">
        <div>
          <span className="mb-1 block text-xs text-gray-500">Max spend</span>
          {/* Single bordered box with the "$" as an in-field suffix on the
              right (borderless), mirroring the token unit selector. */}
          <div className="flex w-32 items-center rounded-lg border border-gray-300 bg-white pr-2 focus-within:border-blue-500 focus-within:ring-1 focus-within:ring-blue-500">
            <input
              type="number" min={0.5} max={SAFETY_CAP_MAX.maxBudgetUsd} step={0.5}
              value={_clampCap(formData.maxBudgetUsd, SAFETY_CAP_DEFAULTS.maxBudgetUsd, SAFETY_CAP_MAX.maxBudgetUsd, { float: true })}
              onChange={(e) => update('maxBudgetUsd', _clampCap(parseFloat(e.target.value), SAFETY_CAP_DEFAULTS.maxBudgetUsd, SAFETY_CAP_MAX.maxBudgetUsd, { float: true }))}
              className="w-full min-w-0 rounded-l-lg border-0 bg-transparent px-3 py-2 text-sm focus:outline-none focus:ring-0"
            />
            <span className="shrink-0 pl-1 text-xs text-gray-600">$</span>
          </div>
        </div>
        <div>
          <span className="mb-1 block text-xs text-gray-500">Max tokens</span>
          <TokenCapInput tokens={formData.maxTokens} onChange={(v) => update('maxTokens', v)} />
        </div>
        <div>
          <span className="mb-1 block text-xs text-gray-500">Max turns</span>
          <input
            type="number" min={1} max={SAFETY_CAP_MAX.maxTurns} step={5}
            value={_clampCap(formData.maxTurns, SAFETY_CAP_DEFAULTS.maxTurns, SAFETY_CAP_MAX.maxTurns)}
            onChange={(e) => update('maxTurns', _clampCap(parseInt(e.target.value, 10), SAFETY_CAP_DEFAULTS.maxTurns, SAFETY_CAP_MAX.maxTurns))}
            className="block w-24 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
          />
        </div>
      </div>
    </div>
  );
}

/** PLAN WORDS (EXPERT-NETWORKS.md §1, E1) — what is saved: {} (the default words) or {kind:'expert', group, item, leaf}
 *  with each label trimmed; the backend falls back to the defaults for anything invalid. */
export function cleanPlanVocabulary(v) {
  if (!v || typeof v !== 'object' || v.kind !== 'expert') return {};
  const t = (x, d) => (String(x || '').replace(/\s+/g, ' ').trim().slice(0, 24) || d);
  return { kind: 'expert', group: t(v.group, 'Team'), item: t(v.item, 'Responsibility'), leaf: t(v.leaf, 'Rule') };
}

export default function AgentConfigEditor({
  agents = {},
  onSave,
  networkId,
  tenantId,
  fieldLock = null,
  currentUserSub = '',
  canForceRelease = false,
  topologyGraph = null,
  // GENERATIVE (Brain2/manifest) networks only: the agent TYPE is switchable — the
  // engine routes worker↔builder off it, and the switch freezes into the next released
  // MINOR (like every other agent-config field). ASL networks keep type read-only (the
  // per-agent worker/builder was hardcoded into the deployed Step Function).
  generative = false,
  // LIVE tier→model family map from the GET-network response ({fast:'Haiku', …}) so the
  // generative-engine tier selects show the ACTUAL models and auto-reflect a model swap.
  // Null → fall back to the static ENGINE_TIER_MODEL constant (offline/older payloads).
  engineTierModels = null,
  // Optional: switch to the Connections tab + auto-open a tool's creator when
  // the tool picker's instance list is empty. ({ tool }) => void. See
  // AgentToolPicker.onNavigateToConnections.
  onNavigateToConnections = null,
  // Round-trip: a connection just created in Connections, to auto-assign to the
  // (still-selected) agent on return. ``{ tool, secretId, secretName }``.
  pendingToolAssign = null,
  onPendingToolAssignConsumed = null,
}) {
  const { showToast } = useToast();
  // Tier→model labels for the generative-engine selects: prefer the LIVE map from the
  // GET-network response (auto-reflects a model swap) and fall back to the static constant
  // per tier so a missing/partial payload still names a model instead of a blank.
  const tierModel = useMemo(() => ({
    fast: engineTierModels?.fast || ENGINE_TIER_MODEL.fast,
    think: engineTierModels?.think || ENGINE_TIER_MODEL.think,
    reason: engineTierModels?.reason || ENGINE_TIER_MODEL.reason,
  }), [engineTierModels]);
  const agentKeys = useMemo(
    () => Object.keys(agents || {}).filter((id) => !ROSTER_EXCLUDED_AGENT_IDS.has(id)),
    [agents]
  );
  // STAGE COUNT for the manual stage-cap control (Shared settings). A generative manifest is a
  // nested org-tree; its "stages" are the topology DEPTH levels (rootCohort = Stage 1, their teams
  // = Stage 2, …). Node ids in ``topologyGraph`` are nodePaths ("root/Lead/Specialist"), so a
  // node's depth = segment count after "root". Max agent-node depth = the number of stages. We
  // don't have the architect's ``phases`` names threaded here, and our target networks are the
  // non-milestone case anyway, so label them generically "Stage 1 / Stage 2 / …".
  const stageCount = useMemo(() => {
    const nodes = Array.isArray(topologyGraph?.nodes) ? topologyGraph.nodes : [];
    let maxDepth = 0;
    for (const n of nodes) {
      const id = String(n?.id || '');
      if (!id.startsWith('root/')) continue;           // skip __start__/__stop__ terminals + bare root
      const depth = id.split('/').length - 1;          // "root/A" → 1, "root/A/B" → 2
      if (depth > maxDepth) maxDepth = depth;
    }
    return maxDepth;
  }, [topologyGraph]);
  // Dropdown shows the runnable agents PLUS the special non-agent "Room context"
  // entry (always offered, even before it's been saved into the map). Selecting
  // it edits the network's room-context injection setting, not an agent.
  const dropdownKeys = useMemo(
    () => [...agentKeys, ROOM_CONTEXT_AGENT_KEY],
    [agentKeys],
  );
  // Default to the "⚙ Shared" (``__roomContext__``) entry — the network-wide settings (Agent mode,
  // Economy, engine tiers, Run depth) are the most common landing intent + let the user see the
  // stage-cap effect on the tree immediately, rather than a specific agent's config.
  const [selectedKey, setSelectedKey] = useState(ROOM_CONTEXT_AGENT_KEY);
  const [formData, setFormData] = useState(null);
  const [originalData, setOriginalData] = useState(null);
  // DIMMED (capped-out) agents for the live tree: logicalIds whose stage-depth exceeds the
  // stageDepthCap. cap 0/Auto → nothing dimmed. The stage cap is a NETWORK-WIDE "Shared" setting,
  // so the tree must reflect it REGARDLESS of which agent is selected — read the SAVED value off
  // ``agents.__roomContext__`` as the base. Only when the user is actively editing the Shared entry
  // do we prefer the live ``formData`` value, so the Stage pills grey the tree instantly as clicked.
  // Keyed by logicalId (node ``data.label``) to match ``ManifestTreeFlow``'s ``nodeAgentId``.
  // Declared AFTER ``formData`` (it reads formData.stageDepthCap) to avoid a TDZ error.
  const stageCapDimmedIds = useMemo(() => {
    const savedCap = Number.parseInt(
      ((agents && agents[ROOM_CONTEXT_AGENT_KEY]) || {}).stageDepthCap, 10,
    ) || 0;
    // Editing the Shared entry → the live draft value wins (responsive pills); else the saved value.
    const liveCap = (selectedKey === ROOM_CONTEXT_AGENT_KEY)
      ? (Number.parseInt(formData?.stageDepthCap, 10) || 0) : savedCap;
    const cap = Math.max(0, liveCap);
    if (!cap) return null;
    const nodes = Array.isArray(topologyGraph?.nodes) ? topologyGraph.nodes : [];
    const out = new Set();
    for (const n of nodes) {
      const id = String(n?.id || '');
      if (!id.startsWith('root/')) continue;
      const depth = id.split('/').length - 1;          // stage = depth
      if (depth > cap) {
        const lid = String(n?.data?.label || '').trim() || id.split('/').pop();
        if (lid) out.add(lid);
      }
    }
    return out.size ? out : null;
  }, [topologyGraph, agents, selectedKey, formData?.stageDepthCap]);

  // AVAILABILITY dim-map for the live tree ({agentId → 'branch'|'self'} or null) — SAME live-vs-
  // saved pattern as ``stageCapDimmedIds`` above, so the graph reacts to a disable/bypass click
  // BEFORE Save, letting the user see the consequence and play around. For each agent we take the
  // SAVED ``disabledMode`` off ``agents[id]``, EXCEPT the currently-selected agent, whose LIVE
  // ``formData.disabledMode`` wins (that's the one the user is toggling). ManifestTreeFlow expands
  // 'branch' to the whole subtree + 'self' to just the node. Keyed by logicalId (node label).
  const disabledByAgentId = useMemo(() => {
    if (!generative) return null;
    const out = {};
    for (const id of agentKeys) {
      const saved = String((agents[id] || {}).disabledMode || '').trim().toLowerCase();
      const live = (id === selectedKey)
        ? String(formData?.disabledMode || '').trim().toLowerCase()
        : saved;
      if (live === 'branch' || live === 'self') out[id] = live;
    }
    return Object.keys(out).length ? out : null;
  }, [generative, agentKeys, agents, selectedKey, formData?.disabledMode]);

  const [saving, setSaving] = useState(false);
  const [toolsCatalog, setToolsCatalog] = useState({ tools: [], loading: true, error: '' });
  const [mcpCatalog, setMcpCatalog] = useState({ mcps: [], loading: true, error: '' });
  // Curated workspaces visible at BUILD time = tenant + this network (pool-scoped
  // ones only apply as room overrides). Drives the build-time defaultWorkspaceId.
  const [workspaceCatalog, setWorkspaceCatalog] = useState([]);

  useEffect(() => {
    let cancelled = false;
    setToolsCatalog((prev) => ({ ...prev, loading: true, error: '' }));
    listTools({ forAgent: true })
      .then((data) => {
        if (cancelled) return;
        setToolsCatalog({ tools: data.tools || [], loading: false, error: '' });
      })
      .catch((err) => {
        if (cancelled) return;
        setToolsCatalog({
          tools: [],
          loading: false,
          error: err?.message || 'Failed to load tools',
        });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Platform MCP allowlist — drives the "enable MCP" toggles. Read-only
  // catalog; a missing endpoint just yields no toggles (graceful).
  useEffect(() => {
    let cancelled = false;
    setMcpCatalog((prev) => ({ ...prev, loading: true, error: '' }));
    listMcpRegistry()
      .then((data) => {
        if (cancelled) return;
        setMcpCatalog({ mcps: data.mcps || [], loading: false, error: '' });
      })
      .catch((err) => {
        if (cancelled) return;
        setMcpCatalog({ mcps: [], loading: false, error: err?.message || 'Failed to load MCPs' });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Curated workspaces for the build-time default — tenant + this network
  // (listWorkspaces walks the visibility pyramid from networkId). Empty list
  // just hides the workspace option in the picker (graceful).
  useEffect(() => {
    let cancelled = false;
    listWorkspaces({ networkId: networkId || undefined })
      .then((data) => {
        if (cancelled) return;
        const rows = Array.isArray(data?.secrets) ? data.secrets : [];
        setWorkspaceCatalog(rows.map((r) => ({
          id: r.secretId, label: r.secretName || r.secretId, description: '',
          scope: r.scope, // for the picker's scope badge (Room only / Network / Tenant)
        })));
      })
      .catch(() => { if (!cancelled) setWorkspaceCatalog([]); });
    return () => { cancelled = true; };
  }, [networkId]);

  const selectedAgent = selectedKey ? agents[selectedKey] : null;

  // Does the selected agent hold a team? A LEAF can't be "bypassed" (self) — there's no team to
  // keep alive — so the Availability control offers Disable/Enable only for leaves (mirrors the
  // engine's leaf-coercion). Team = any topology node whose path is a child of this agent's node.
  const selectedHasTeam = useMemo(() => {
    if (!selectedKey || selectedKey === ROOM_CONTEXT_AGENT_KEY) return false;
    const nodes = Array.isArray(topologyGraph?.nodes) ? topologyGraph.nodes : [];
    const own = nodes.find((n) => String(n?.data?.label || '').trim() === selectedKey);
    const ownId = String(own?.id || '');
    if (!ownId) return false;
    return nodes.some((n) => String(n?.id || '').startsWith(`${ownId}/`));
  }, [selectedKey, topologyGraph]);

  // Room-shared tools / MCPs / workspace this agent INHERITS (doc/arch/ASSETS.md).
  // Build-time mirror of the runtime union: show the room context's tools as
  // read-only "Inherited" chips above the agent's own picker, so the creator
  // sees the effective set without it being editable here (edit it on ⚙ Room
  // context). De-duped against what the agent already pins itself.
  const inheritedRoomTools = useMemo(() => {
    if (!selectedKey || selectedKey === ROOM_CONTEXT_AGENT_KEY) return [];
    const rc = (agents && agents[ROOM_CONTEXT_AGENT_KEY]) || {};
    const labelOf = (e) => (typeof e === 'string'
      ? e
      : String((e && (e.instanceLabel || e.name || e.id)) || '')).trim();
    // MCP id ignoring instance — an agent that owns ANY instance of an MCP id
    // must NOT also show the Shared tier's instance of the SAME id (one agent,
    // two githubs is ambiguous; the agent's own wins at runtime). Connectors
    // still dedup by label (multiple credentialed instances are legitimate).
    const idOf = (e) => (typeof e === 'string'
      ? e
      : String((e && (e.id || e.name)) || '')).trim();
    const ownToolKeys = new Set(
      (Array.isArray(formData?.tools) ? formData.tools : []).map(labelOf).filter(Boolean),
    );
    const ownMcpIds = new Set(
      (Array.isArray(formData?.enabledMcpIds) ? formData.enabledMcpIds : []).map(idOf).filter(Boolean),
    );
    const out = [];
    for (const t of (Array.isArray(rc.tools) ? rc.tools : [])) {
      const l = labelOf(t);
      if (l && !ownToolKeys.has(l)) out.push({ kind: 'tool', label: l });
    }
    for (const m of (Array.isArray(rc.enabledMcpIds) ? rc.enabledMcpIds : [])) {
      const id = idOf(m);
      if (id && !ownMcpIds.has(id)) out.push({ kind: 'mcp', label: labelOf(m) });
    }
    return out;
  }, [selectedKey, agents, formData?.tools, formData?.enabledMcpIds]);

  const inheritedRoomWorkspace = useMemo(() => {
    if (!selectedKey || selectedKey === ROOM_CONTEXT_AGENT_KEY) return '';
    // SHARED NETWORK WORKSPACE is UNIQUE + GLOBAL: when the Shared row assigns one it is
    // pinned room-wide and OVERRIDES any per-agent workspace at runtime (hydrate: pool-wide
    // pin > per-agent default). So — unlike the old "own wins" rule — the network workspace
    // ALWAYS shows as the effective inherited one, even if this agent still carries a stale
    // own ``defaultWorkspaceId`` (which is cleared on save; see ``perAgentWorkspaceLocked``).
    const rc = (agents && agents[ROOM_CONTEXT_AGENT_KEY]) || {};
    const wsId = String(rc.defaultWorkspaceId || '').trim();
    if (!wsId) return '';
    const match = (workspaceCatalog || []).find((w) => String(w.id) === wsId);
    return match ? (match.label || wsId) : wsId;
  }, [selectedKey, agents, workspaceCatalog]);

  // SHARED NETWORK WORKSPACE (CORE-AGENTS.md §4.4): the workspace assigned on the
  // Shared (``__roomContext__``) row. When set, it's pinned ROOM-WIDE at release and
  // OVERRIDES any per-agent ``defaultWorkspaceId`` at runtime (hydrate precedence:
  // pool-wide pin > per-agent build-time default). So a per-agent workspace pick would
  // be DEAD — offering it is a silent redundant trap. We read it live off ``formData``
  // when the user is editing the Shared row (so the per-agent picker locks the instant
  // they assign one), else off the saved Shared row. Empty → no network workspace, the
  // per-agent picker is live as before. Returns the human name (or id) for the note.
  const networkSharedWorkspace = useMemo(() => {
    const savedRc = (agents && agents[ROOM_CONTEXT_AGENT_KEY]) || {};
    const wsId = (selectedKey === ROOM_CONTEXT_AGENT_KEY)
      ? String(formData?.defaultWorkspaceId || '').trim()
      : String(savedRc.defaultWorkspaceId || '').trim();
    if (!wsId) return '';
    const match = (workspaceCatalog || []).find((w) => String(w.id) === wsId);
    return match ? (match.label || wsId) : wsId;
  }, [selectedKey, agents, formData?.defaultWorkspaceId, workspaceCatalog]);
  // Lock the PER-AGENT workspace picker when a network workspace is set AND we're on a
  // real agent (not the Shared row, where the network workspace IS assigned/edited).
  const perAgentWorkspaceLocked = !!(networkSharedWorkspace && selectedKey !== ROOM_CONTEXT_AGENT_KEY);

  // Re-seed the form from the saved config when the SELECTED AGENT changes, or
  // when its saved content genuinely changes underneath us. Crucially we do NOT
  // re-seed on every new ``agents`` object identity: the workbench re-derives
  // ``network.agents`` on AppSync refreshes, handing us a fresh reference with
  // identical content — blindly re-seeding there wiped an in-progress edit
  // (e.g. a just-added MCP) and flipped Save back to disabled. We compare by
  // content and never clobber unsaved local edits (``isDirty``); a real peer
  // takeover is handled separately by the field-lock effect above.
  const seededKeyRef = useRef(null);
  useEffect(() => {
    // Room-context entry: seed the injection setting (NOT agent defaults).
    // Default ON ('room') — only an EXPLICIT 'off' opts out. Mirrors the backend
    // default in pool_memory._network_room_context_mode.
    if (selectedKey === ROOM_CONTEXT_AGENT_KEY) {
      const raw = (agents && agents[ROOM_CONTEXT_AGENT_KEY]) || {};
      const inj = raw.injection === 'off' ? 'off' : 'room';
      const jm = ['fast', 'think', 'reason'].includes(raw.judgeModel) ? raw.judgeModel : '';
      // Room-shared tools / MCPs / default workspace every agent inherits
      // (doc/arch/ASSETS.md). Stored on this reserved row, so they freeze with
      // the release and resolve through the same chokepoint as agent tools.
      const _tier = (v) => (['fast', 'think', 'reason'].includes(v) ? v : '');
      const merged = {
        injection: inj,
        judgeModel: jm,
        // Generative-engine model tiers (SEPARATE from judgeModel above). Stored on this
        // reserved row so they freeze with the release; '' = engine default.
        engineLeadTier: _tier(raw.engineLeadTier),
        engineJudgeTier: _tier(raw.engineJudgeTier),
        engineFinalTier: _tier(raw.engineFinalTier),
        engineJudgeMode: ['on', 'off'].includes(raw.engineJudgeMode) ? raw.engineJudgeMode : '',
        // REDUCER-ON-CLAUDE-CODE switches (CLAUDE-CODE-MIGRATION.md Phases 6/7) — do the engine
        // reducers reason on claude-code (a workspace-reading agent that decomposes epics into
        // cohesive subtasks + synthesizes) vs a single-shot call? Stored on this reserved row so they
        // FREEZE with the release like the tiers. f.synth (final) DEFAULTS ON (the task-graph author);
        // n.synth (node/per-node judge) DEFAULTS OFF (the per-node cost multiplier). An explicit bool
        // is persisted (frozen); a legacy row with no value falls to the engine default.
        finalReducerClaudeCode: typeof raw.finalReducerClaudeCode === 'boolean'
          ? raw.finalReducerClaudeCode : true,
        nodeReducerClaudeCode: typeof raw.nodeReducerClaudeCode === 'boolean'
          ? raw.nodeReducerClaudeCode : false,
        // GLOBAL 'Agent Mode' control-tower — applies to EVERY agent, superseding each
        // agent's own capability. 'dual' (default) = Lead picks worker/builder per request;
        // 'worker'/'builder' force that kind everywhere; 'agentConfig' defers to each agent.
        agentMode: ['dual', 'worker', 'builder', 'agentConfig'].includes(raw.agentMode) ? raw.agentMode : 'dual',
        // ROOM TYPE (MODES.md §8) — frozen with the release: 'project' (default — Context · Plan · Run · Verify) or
        // 'context' (Context only: discussion, Build context, direct work; no network runs).
        roomType: raw.roomType === 'context' ? 'context' : 'project',
        // PLAN WORDS (EXPERT-NETWORKS.md §1, E1) — frozen with the release: what the plan tiers are called.
        // standard = Milestone → Epic → Task; expert = Team → Responsibility → Rule; any labels the network owns.
        planVocabulary: raw.planVocabulary && typeof raw.planVocabulary === 'object' ? { ...raw.planVocabulary } : {},
        // ECONOMY / TEST MODE — network-wide cheap-run toggle. Forces the cheapest tier +
        // tight output caps on the engine AND every worker. DEFAULT ON (unset → on); the
        // user opts OUT for a full-quality run. Off only when explicitly saved off.
        economyMode: raw.economyMode == null
          ? true
          : (raw.economyMode === true
            || !['off', 'false', '0', 'no'].includes(String(raw.economyMode).toLowerCase())),
        // VERBOSE — cost-telemetry toggle. DEFAULT OFF (unset → off), the mirror image of
        // economyMode above: the user opts IN to see the per-agent cost breakdown. Only an
        // explicit truthy value turns it on, so a garbled flag stays quiet rather than surfacing
        // developer chrome to an end user.
        verbose: raw.verbose === true
          || ['on', 'true', '1', 'yes'].includes(String(raw.verbose ?? '').trim().toLowerCase()),
        // GET REASONING (debug) — same fail-quiet default-OFF as verbose: only an explicit truthy
        // value turns it on, so it never surfaces (or costs tokens) by accident.
        getReasoning: raw.getReasoning === true
          || ['on', 'true', '1', 'yes'].includes(String(raw.getReasoning ?? '').trim().toLowerCase()),
        // MANUAL STAGE CAP — 0 = Auto (Lead/reducer decide depth, default); N>0 caps the engine to
        // the first N stages ("as if deeper layers don't exist"). Frozen with the release. Coerce
        // any legacy/garbage value to a non-negative int (0 = auto).
        stageDepthCap: Math.max(0, Number.parseInt(raw.stageDepthCap, 10) || 0),
        tools: Array.isArray(raw.tools) ? raw.tools : [],
        enabledMcpIds: Array.isArray(raw.enabledMcpIds) ? raw.enabledMcpIds : [],
        defaultWorkspaceId: typeof raw.defaultWorkspaceId === 'string' ? raw.defaultWorkspaceId : '',
      };
      const keyChanged = seededKeyRef.current !== selectedKey;
      const locallyDirty = !!(formData && originalData && !deepEqual(formData, originalData));
      const contentChanged = !!originalData && !deepEqual(merged, originalData);
      if (keyChanged) {
        setFormData(merged);
        setOriginalData(JSON.parse(JSON.stringify(merged)));
        seededKeyRef.current = selectedKey;
      } else if (contentChanged && !locallyDirty) {
        setFormData(merged);
        setOriginalData(JSON.parse(JSON.stringify(merged)));
      }
      return;
    }
    if (!selectedKey || !agents[selectedKey]) {
      setFormData(null);
      setOriginalData(null);
      seededKeyRef.current = null;
      return;
    }
    const merged = mergeWithDefaults(agents[selectedKey]);
    const keyChanged = seededKeyRef.current !== selectedKey;
    // Local edits in flight? (computed inline — ``isDirty`` is declared below.)
    const locallyDirty = !!(formData && originalData && !deepEqual(formData, originalData));
    // Saved content moved (a real server-side change), as opposed to just a new
    // object reference with the same content.
    const contentChanged = !!originalData && !deepEqual(merged, originalData);
    if (keyChanged) {
      setFormData(merged);
      setOriginalData(JSON.parse(JSON.stringify(merged)));
      seededKeyRef.current = selectedKey;
    } else if (contentChanged && !locallyDirty) {
      // Adopt the upstream change only when we have no unsaved edits to lose.
      setFormData(merged);
      setOriginalData(JSON.parse(JSON.stringify(merged)));
    }
  }, [selectedKey, agents, formData, originalData]);

  useEffect(() => {
    // The room-context entry is always a valid selection (it's not in agentKeys).
    if (selectedKey === ROOM_CONTEXT_AGENT_KEY) return;
    if (agentKeys.length === 0) {
      if (selectedKey != null) setSelectedKey(null);
      return;
    }
    if (!selectedKey || !agentKeys.includes(selectedKey)) {
      setSelectedKey(agentKeys[0]);
    }
  }, [agentKeys, selectedKey]);

  const isDirty = useMemo(() => {
    if (!formData || !originalData) return false;
    return !deepEqual(formData, originalData);
  }, [formData, originalData]);

  // TWO-WAY selection: clicking an agent node on the topology graph selects it exactly like
  // picking it from the "Select Agent" dropdown — the config form loads and the dropdown shows
  // the agent (one shared ``selectedKey``; the graph highlight follows automatically). The graph
  // hands us a logicalId (``nodeAgentId``: node label, else last path segment), which is the same
  // key the dropdown uses. Guards: ignore ids not in the roster (``__start__``/``__stop__``
  // terminals, stale labels) and the reserved room-context key (it has no node anyway) — and a
  // re-click of the already-selected agent is a no-op so it can't clobber in-progress edits.
  const handleGraphAgentClick = useCallback((agentId) => {
    const k = String(agentId || '').trim();
    if (!k || k === selectedKey) return;
    if (!agentKeys.includes(k)) return;   // not a configurable agent node
    setSelectedKey(k);
  }, [agentKeys, selectedKey]);

  // -----------------------------------------------------------------
  // Collaborative field-lock plumbing
  // -----------------------------------------------------------------
  //
  // We lock at **agent granularity** (one lock per ``agent:{logicalId}``)
  // rather than per form field. Per-field would pepper the UI with 20+
  // small lock indicators for what is semantically a single editing
  // session; per-agent mirrors the user's mental model ("I'm editing the
  // Finance Agent") and matches how the backend emits FIELD_UPDATED.
  const lockField = selectedKey ? `agent:${selectedKey}` : null;
  const currentLock = lockField && fieldLock ? fieldLock.getLock(lockField) : null;
  const isSelfHolder = !!(
    currentLock && currentUserSub && currentLock.holderSub === currentUserSub
  );
  const isPeerHolder = !!(currentLock && !isSelfHolder);
  const heldSelfRef = useRef(false);
  const refreshTimerRef = useRef(null);

  // Release whatever lock we hold. Safe to call on paths where we never
  // acquired one — the API returns 200 with ``released:false`` in that case.
  const releaseSelfLock = useCallback(
    (fieldPath) => {
      if (!fieldPath || !fieldLock) return;
      if (!heldSelfRef.current) return;
      heldSelfRef.current = false;
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
      void fieldLock.release(fieldPath);
    },
    [fieldLock],
  );

  // When the operator switches to a different agent or the editor
  // unmounts, drop any lock we still hold so peers aren't blocked on a
  // stale session. The ref guards double-release under React StrictMode.
  useEffect(() => {
    const priorField = lockField;
    return () => {
      releaseSelfLock(priorField);
    };
  }, [lockField, releaseSelfLock]);

  // A peer took the lock → bail out of editing mode, revert to the
  // last-saved snapshot. This is deliberately destructive (last-writer-wins
  // only while you hold the lock); better to lose a few keystrokes than to
  // overwrite a peer's concurrent save.
  useEffect(() => {
    if (!isPeerHolder) return;
    if (heldSelfRef.current) {
      heldSelfRef.current = false;
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    }
    if (originalData) {
      setFormData(JSON.parse(JSON.stringify(originalData)));
    }
  }, [isPeerHolder, originalData]);

  const update = (path, value) => {
    // First keystroke on the agent attempts to take the lock. If a peer
    // owns it, ``acquire`` throws with status 409 and the 409 lock object
    // is surfaced through ``fieldLock.locksByField`` — we swallow the
    // throw here and let the peer-lock effect above roll the form back.
    if (fieldLock && lockField && !heldSelfRef.current) {
      heldSelfRef.current = true;
      fieldLock
        .acquire(lockField)
        .then(() => {
          if (refreshTimerRef.current) return;
          refreshTimerRef.current = setInterval(() => {
            if (!heldSelfRef.current) return;
            fieldLock.acquire(lockField).catch(() => undefined);
          }, LOCK_REFRESH_INTERVAL_MS);
        })
        .catch(() => {
          heldSelfRef.current = false;
        });
    }

    setFormData((prev) => {
      if (!prev) return prev;
      const next = JSON.parse(JSON.stringify(prev));
      const parts = path.split('.');
      let obj = next;
      for (let i = 0; i < parts.length - 1; i++) {
        const p = parts[i];
        if (!(p in obj)) obj[p] = {};
        obj = obj[p];
      }
      obj[parts[parts.length - 1]] = value;
      return next;
    });
  };

  /** The per-agent 'Agent Type' dropdown is 3 UI options that map to the TWO stored fields
   *  the backend uses (``type`` = default kind, ``capabilityMode`` = may-flex):
   *    worker  → type=AI,                capabilityMode='worker-only'
   *    builder → type=UNIVERSAL_BUILDER, capabilityMode='builder-only'
   *    dual    → type=AI (cheap default), capabilityMode='dual' (Lead flexes per request)
   *  Both are set in one lock-taking update so they never drift apart. */
  const updateAgentKind = (kind) => {
    update('type', kind === 'builder' ? 'UNIVERSAL_BUILDER' : 'AI');
    update('capabilityMode', kind === 'builder' ? 'builder-only' : kind === 'dual' ? 'dual' : 'worker-only');
  };

  /** Current 3-option value derived from the stored fields. capabilityMode wins (it's the
   *  richer signal); fall back to ``type`` for legacy rows with no capabilityMode. */
  const agentKindValue = (() => {
    const cm = String(formData?.capabilityMode || '').trim().toLowerCase();
    if (cm === 'dual') return 'dual';
    if (cm === 'builder-only') return 'builder';
    if (cm === 'worker-only') return 'worker';
    return (formData?.type || 'AI') === 'UNIVERSAL_BUILDER' ? 'builder' : 'worker';
  })();

  const handleSave = async () => {
    if (!isDirty || !formData || !selectedKey || !networkId) return;
    if (isPeerHolder) {
      showToast('Another editor holds this agent — refresh to see their changes', 'error');
      return;
    }
    setSaving(true);
    try {
      // Room-context entry is NOT an agent: save just the injection setting (no
      // tools normalization, no agent defaults). It writes under the reserved
      // key so it versions with the snapshot like everything else.
      if (selectedKey === ROOM_CONTEXT_AGENT_KEY) {
        const inj = formData.injection === 'off' ? 'off' : 'room';
        const jm = ['fast', 'think', 'reason'].includes(formData.judgeModel) ? formData.judgeModel : '';
        // Room-shared tools / MCPs / workspace ride on the same reserved-key
        // PUT (the backend validates tools like any agent row, routes judgeModel
        // to its own row, and freezes the rest into the snapshot).
        const _tier = (v) => (['fast', 'think', 'reason'].includes(v) ? v : '');
        const payload = {
          injection: inj,
          judgeModel: jm,
          // Generative-engine tiers (frozen with the release; separate from judgeModel).
          engineLeadTier: _tier(formData.engineLeadTier),
          engineJudgeTier: _tier(formData.engineJudgeTier),
          engineFinalTier: _tier(formData.engineFinalTier),
          engineJudgeMode: ['on', 'off'].includes(formData.engineJudgeMode) ? formData.engineJudgeMode : '',
          // REDUCER-ON-CLAUDE-CODE switches (frozen with the release). Persisted as explicit bools so
          // the room version pins them; env still overrides at runtime. f.synth default ON, n.synth OFF.
          finalReducerClaudeCode: formData.finalReducerClaudeCode === true,
          nodeReducerClaudeCode: formData.nodeReducerClaudeCode === true,
          // GLOBAL 'Agent Mode' control-tower (frozen with the release; supersedes each
          // agent's capabilityMode at runtime). Default 'dual'.
          agentMode: ['dual', 'worker', 'builder', 'agentConfig'].includes(formData.agentMode) ? formData.agentMode : 'dual',
          // ROOM TYPE (MODES.md §8) — frozen with the release (a room runs its pinned release's type).
          roomType: formData.roomType === 'context' ? 'context' : 'project',
          // PLAN WORDS (EXPERT-NETWORKS.md §1, E1) — frozen with the release.
          planVocabulary: cleanPlanVocabulary(formData.planVocabulary),
          // ECONOMY / TEST MODE — frozen with the release; engine + workers clamp when on.
          economyMode: formData.economyMode === true,
          // VERBOSE — frozen with the release; run blocks show the cost breakdown only when on.
          verbose: formData.verbose === true,
          // GET REASONING (debug) — frozen with the release; enables decision-maker chain-of-thought.
          getReasoning: formData.getReasoning === true,
          // MANUAL STAGE CAP — frozen with the release; 0 = auto (no cap), N>0 caps engine depth.
          stageDepthCap: Math.max(0, Number.parseInt(formData.stageDepthCap, 10) || 0),
          tools: Array.isArray(formData.tools) ? formData.tools : [],
          enabledMcpIds: Array.isArray(formData.enabledMcpIds) ? formData.enabledMcpIds : [],
          defaultWorkspaceId: String(formData.defaultWorkspaceId || ''),
        };
        await onSave(networkId, selectedKey, payload);
        setOriginalData(JSON.parse(JSON.stringify(payload)));
        showToast('Room context setting saved', 'success');
        releaseSelfLock(lockField);
        return;
      }
      const toolsPayload = Array.isArray(formData.tools) ? formData.tools : [...defaultAgent().tools];
      const payload = { ...formData, tools: toolsPayload };
      // SHARED NETWORK WORKSPACE: when locked, the per-agent workspace is dead (the room-wide
      // network pin overrides it at runtime). Clear any stale value so it doesn't get frozen
      // into the release as a confusing no-op — the picker is hidden so the user can't clear it.
      if (perAgentWorkspaceLocked) payload.defaultWorkspaceId = '';
      await onSave(networkId, selectedKey, payload);
      setOriginalData(JSON.parse(JSON.stringify({ ...payload, tools: [...toolsPayload] })));
      showToast('Agent saved successfully', 'success');
      // Releasing on save gives peers an instant green light — the next
      // edit will re-acquire a fresh 120s lease, so no UX regression for
      // the current user either.
      releaseSelfLock(lockField);
    } catch (err) {
      showToast(err?.message || 'Failed to save agent', 'error');
    } finally {
      setSaving(false);
    }
  };

  if (agentKeys.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center p-8 bg-gray-50">
        <div className="text-center text-gray-500">
          <p className="font-medium">No agents in this network</p>
          <p className="text-sm mt-1">Deploy a network with agents to configure them here.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col overflow-hidden lg:flex-row">
      {/* Left: roster + agent form (fills width on narrow viewports). */}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
      {/* Master: Agent selector */}
      <div className="flex-shrink-0 px-6 py-4 border-b border-gray-200 bg-white">
        <label className="block text-sm font-medium text-gray-700 mb-2">Select Agent</label>
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={selectedKey || ''}
            onChange={(e) => setSelectedKey(e.target.value || null)}
            className="block w-auto max-w-full rounded-lg border border-gray-300 bg-white pl-3 pr-8 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
          >
            {dropdownKeys.map((k) => (
              <option key={k} value={k}>
                {k === ROOM_CONTEXT_AGENT_KEY ? '⚙ Shared' : k}
              </option>
            ))}
          </select>
          <FieldLockBadge
            lock={currentLock}
            isSelf={isSelfHolder}
            canForceRelease={canForceRelease && isPeerHolder}
            onForceRelease={() => fieldLock && lockField && fieldLock.forceRelease(lockField)}
          />
          {/* AVAILABILITY (ROOM-SCOPES) — moved beside the Select Agent dropdown so the carve
              control sits with agent selection, not buried below the config tabs. A per-agent
              config field saved by "Save changes" + FROZEN into the release footprint like
              ``agentType`` / ``stageDepthCap`` (no manifest touch). ``disabledMode`` ∈ ''(enabled)
              | 'branch' (disable agent + its whole team) | 'self' (bypass just this agent; team
              still runs). Set via ``update`` so it rides the normal dirty/lock/save flow.
              Generative only; a LEAF can't be bypassed (no team) so it shows Disable/Enable only.
              Skipped for the ⚙ Shared row (not an agent). */}
          {generative && selectedKey !== ROOM_CONTEXT_AGENT_KEY && formData && (() => {
            const mode = String(formData.disabledMode || '').trim().toLowerCase();
            const opts = [
              { v: '', label: 'Enabled' },
              { v: 'branch', label: 'Disable node + team' },
              ...(selectedHasTeam ? [{ v: 'self', label: 'Bypass agent' }] : []),
            ];
            return (
              <div className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5">
                <span className="text-xs font-semibold text-amber-900">Availability in room:</span>
                {opts.map((o) => (
                  <button
                    key={o.v || 'enabled'}
                    type="button"
                    disabled={isPeerHolder}
                    onClick={() => update('disabledMode', o.v)}
                    className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors disabled:opacity-50 ${
                      mode === o.v
                        ? 'bg-amber-600 text-white shadow-sm'
                        : 'bg-white text-amber-800 border border-amber-300 hover:bg-amber-100'
                    }`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            );
          })()}
        </div>
      </div>

      {/* Room context (network setting) — NOT an agent. A small panel to pick
          the injection mode; saved under the reserved key so it versions with
          the snapshot. OFF by default. */}
      {selectedKey === ROOM_CONTEXT_AGENT_KEY && formData && (
        <fieldset
          disabled={isPeerHolder}
          className={`flex-1 min-h-0 flex flex-col ${isPeerHolder ? 'opacity-60' : ''}`}
          data-testid="room-context-panel"
        >
          <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5">
          {/* ECONOMY / TEST MODE — network-wide cheap-run toggle. Applies to EVERY LLM call
              (the generative engine's Lead/Judge/Final reducers AND every worker agent turn):
              forces the cheapest model tier + a tight output-token cap. Output tokens are the
              dominant cost, so this is the big saver for iterating on a network before a full
              run. Frozen with the release like the other Shared settings. */}
          <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50/60 p-4">
            <label className="flex cursor-pointer items-start gap-3">
              <input
                type="checkbox"
                checked={formData.economyMode === true}
                onChange={(e) => update('economyMode', e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                data-testid="shared-economy-mode"
              />
              <span className="min-w-0">
                <span className="block text-sm font-semibold text-amber-900">
                  Economy / Test mode
                </span>
                <span className="mt-0.5 block text-xs text-amber-800">
                  On by default so networks run cheap. Uses the fastest, lowest-cost model and
                  asks agents for complete-but-concise answers — typically 5–10× cheaper per
                  run. Any model tier you explicitly pick below is still honored. Turn OFF for
                  a full-quality run (longer, higher-tier responses). Takes effect on the next
                  released version.
                </span>
              </span>
            </label>
          </div>
          {/* MANUAL STAGE CAP — how many stages of the org-tree the engine is allowed to run.
              "Auto" (default) lets the Lead + node-reducer judge decide depth as-is. Picking a
              stage caps it: the engine runs the first N layers and treats anything deeper as if it
              doesn't exist. Cumulative — selecting "Stage 2" implies Stages 1–2 all run. Only for a
              generative (manifest) network with more than one layer. Frozen with the release. */}
          {generative && stageCount > 1 && (
            <div className="mb-6 border-b border-gray-200 pb-5">
              <h3 className="text-sm font-semibold text-gray-900">Run depth (stages)</h3>
              <p className="mt-1 max-w-prose text-xs text-gray-500">
                How far down the agent tree a run may go. <span className="font-medium">Auto</span> lets
                the Lead decide per request (recommended). Or cap it to the first N stages — the engine
                runs those layers and stops, as if the deeper agents don’t exist. Takes effect on the
                next released version.
              </p>
              <div className="mt-3 flex flex-wrap items-center gap-2" role="group" aria-label="Run depth">
                {(() => {
                  const cap = Math.max(0, Number.parseInt(formData.stageDepthCap, 10) || 0);
                  const pill = (active) => `rounded-full border px-3 py-1 text-xs font-medium transition-colors ${
                    active
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-300 bg-white text-gray-600 hover:border-gray-400'
                  }`;
                  return (
                    <>
                      <button
                        type="button"
                        onClick={() => update('stageDepthCap', 0)}
                        className={pill(cap === 0)}
                        aria-pressed={cap === 0}
                        data-testid="shared-stage-cap-auto"
                      >
                        Auto
                      </button>
                      {Array.from({ length: stageCount }, (_, i) => i + 1).map((n) => {
                        // Cumulative: a stage is "on" when the cap includes it (cap >= n). Clicking a
                        // stage sets the cap to N (Stages 1..N run); clicking the current cap clears
                        // to Auto so it's a toggle.
                        const on = cap >= n;
                        return (
                          <button
                            key={n}
                            type="button"
                            onClick={() => update('stageDepthCap', cap === n ? 0 : n)}
                            className={pill(on)}
                            aria-pressed={on}
                            data-testid={`shared-stage-cap-${n}`}
                          >
                            {`Stage ${n}`}
                          </button>
                        );
                      })}
                    </>
                  );
                })()}
              </div>
              <p className="mt-1.5 text-xs text-gray-500">
                {(() => {
                  const cap = Math.max(0, Number.parseInt(formData.stageDepthCap, 10) || 0);
                  if (cap === 0) return 'Auto — the Lead and reducer decide how deep each run goes.';
                  if (cap >= stageCount) return `All ${stageCount} stages may run (same as Auto's maximum depth).`;
                  return `Capped to Stages 1–${cap}. The engine won’t descend past Stage ${cap}.`;
                })()}
              </p>
            </div>
          )}
          {/* GLOBAL 'Agent Mode' control-tower — network-wide, supersedes each agent's own
              type. Default Dual (the Lead decides per request). Only meaningful for a
              generative (manifest) network; the runtime engine reads it. */}
          {generative && (
            <div className="mb-6 border-b border-gray-200 pb-5">
              <h3 className="text-sm font-semibold text-gray-900">Agent mode (whole network)</h3>
              <p className="mt-1 max-w-prose text-xs text-gray-500">
                One lever for how EVERY agent runs. Supersedes each agent’s own type on the
                Agents tab. Takes effect on the next released version.
              </p>
              <select
                value={formData.agentMode || 'dual'}
                onChange={(e) => update('agentMode', e.target.value)}
                className="mt-3 block w-full max-w-sm rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                data-testid="shared-agent-mode"
              >
                <option value="dual">Dual — the Lead picks worker or builder per request (recommended)</option>
                <option value="worker">Worker — every agent runs fast/cheap (no repo or shell)</option>
                <option value="builder">Builder — every agent runs with repo + shell + CLI</option>
                <option value="agentConfig">Per agent — use each agent’s own type (set on the Agents tab)</option>
              </select>
              <p className="mt-1.5 text-xs text-gray-500">
                {formData.agentMode === 'worker' && 'Fastest and cheapest. Agents that need a repo/shell will be limited.'}
                {formData.agentMode === 'builder' && 'Highest capability, but every agent spins a workspace — slower and costlier.'}
                {formData.agentMode === 'agentConfig' && 'Each agent runs at the type you set on its Tools & Routing tab.'}
                {(!formData.agentMode || formData.agentMode === 'dual') && 'The Lead decides worker vs builder for each agent, each request — best balance of cost and capability.'}
              </p>
            </div>
          )}
          {/* ROOM TYPE (MODES.md §8) — what the network's rooms offer. Frozen with the release: a room runs its
              pinned release's type; upgrading the room changes it. Nothing is deleted when it changes. */}
          <div className="mb-6 border-b border-gray-200 pb-5">
            <h3 className="text-sm font-semibold text-gray-900">Room type</h3>
            <p className="mt-1 max-w-prose text-xs text-gray-500">
              What this network's rooms offer. Takes effect on the next released version.
            </p>
            <select
              value={formData.roomType === 'context' ? 'context' : 'project'}
              onChange={(e) => update('roomType', e.target.value)}
              className="mt-3 block w-full max-w-sm rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
              data-testid="shared-room-type"
            >
              <option value="project">Project — Context · Plan · Run · Verify (recommended)</option>
              <option value="context">Context only — talk it through and work with agents directly</option>
            </select>
            <p className="mt-1.5 text-xs text-gray-500">
              {formData.roomType === 'context'
                ? 'Rooms only offer Context: the discussion, building the context, and asking agents to work on it directly. The network never runs from these rooms; a plan made earlier stays hidden, not deleted.'
                : 'Rooms move through the whole journey: talk it through, plan, run the tasks, verify the work.'}
            </p>
          </div>
          {/* PLAN WORDS (EXPERT-NETWORKS.md §1, E1) — an Expert Network names its plan Team → Responsibility → Rule
              (or its own words). Display only: the plan tree and its gates are the same. Frozen with the release. */}
          <div className="mb-6 border-b border-gray-200 pb-5" data-testid="shared-plan-words">
            <h3 className="text-sm font-semibold text-gray-900">Expert Network</h3>
            <p className="mt-1 max-w-prose text-xs text-gray-500">
              An Expert Network holds a discipline's best practice — its plan is organised as teams of experts, what
              each team is responsible for, and the rules they keep. Takes effect on the next released version.
            </p>
            <label className="mt-3 flex items-center gap-2 text-sm text-gray-800">
              <input type="checkbox" data-testid="shared-expert-network"
                checked={(formData.planVocabulary || {}).kind === 'expert'}
                onChange={(e) => update('planVocabulary', e.target.checked
                  ? { kind: 'expert', group: 'Team', item: 'Responsibility', leaf: 'Rule' } : {})} />
              This is an Expert Network
            </label>
            {(formData.planVocabulary || {}).kind === 'expert' ? (
              <div className="mt-3 grid max-w-md grid-cols-3 gap-2" data-testid="shared-plan-words-labels">
                {[['group', 'Instead of Milestone'], ['item', 'Instead of Epic'], ['leaf', 'Instead of Task']].map(([k, hint]) => (
                  <label key={k} className="text-[11px] text-gray-500">{hint}
                    <input value={(formData.planVocabulary || {})[k] || ''} data-testid={`shared-plan-word-${k}`}
                      onChange={(e) => update('planVocabulary', { ...(formData.planVocabulary || {}), [k]: e.target.value })}
                      className="mt-1 block w-full rounded-md border border-gray-300 px-2 py-1 text-sm text-gray-800" />
                  </label>
                ))}
              </div>
            ) : null}
          </div>
          <h3 className="text-sm font-semibold text-gray-900">Room context injection</h3>
          <p className="mt-1 max-w-prose text-xs text-gray-500">
            When a network run succeeds, its overall context can be recompiled
            (asynchronously) and injected — read-only — into the next run, so agents
            build on the room's accumulated state instead of starting cold. On by
            default for better results; set Off to keep runs independent. Room
            owners can still override this per-room.
          </p>
          <div className="mt-4 space-y-2" role="radiogroup" aria-label="Room context injection mode">
            {ROOM_CONTEXT_INJECTION_OPTIONS.map((opt) => {
              const checked = (formData.injection === 'off' ? 'off' : 'room') === opt.value;
              return (
                <label
                  key={opt.value}
                  className={`flex cursor-pointer items-start gap-3 rounded-lg border p-3 transition-colors ${
                    checked ? 'border-blue-400 bg-blue-50/50 ring-1 ring-blue-200' : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="roomContextInjection"
                    value={opt.value}
                    checked={checked}
                    onChange={() => update('injection', opt.value)}
                    className="mt-0.5"
                    data-testid={`room-context-injection-${opt.value}`}
                  />
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-gray-800">{opt.label}</span>
                    <span className="block text-xs text-gray-500">{opt.desc}</span>
                  </span>
                </label>
              );
            })}
          </div>

          {/* Objective-judge tier (SCHEDULER.md §11.1b) — the NETWORK default
              that rooms inherit when scheduled loops grade against an Objective.
              A room can override it in its own Room-context settings. Model name
              abstracted — capability tiers only. */}
          <div className="mt-5 border-t border-gray-100 pt-4">
            <label className="block text-sm font-medium text-gray-800">Judge intelligence</label>
            <p className="mt-0.5 text-xs text-gray-500">
              Default reasoning tier the judge uses to grade scheduled loops in
              this network's rooms. Higher tiers judge more rigorously; rooms can
              override this.
            </p>
            <select
              value={formData?.judgeModel || ''}
              onChange={(e) => setFormData((d) => ({ ...(d || {}), judgeModel: e.target.value }))}
              data-testid="room-context-judge-model"
              className="mt-2 rounded-md border border-gray-300 px-2 py-1 text-sm"
            >
              <option value="">Default (recommended)</option>
              <option value="fast">Fast — quick, lightweight</option>
              <option value="think">Think — balanced</option>
              <option value="reason">Reason — most rigorous</option>
            </select>
          </div>

          {/* GENERATIVE ENGINE model tiers (generative networks only). DISTINCT from the
              "Judge intelligence" above — that grades scheduled LOOPS; these tune the
              manifest ENGINE that runs the network: the Lead (router), the node-reducer
              judge (per-node quality gate), and the final reducer (synthesis). Stored on
              this reserved row → frozen with the release; '' = engine default. */}
          {generative && (
          <div className="mt-5 border-t border-gray-100 pt-4">
            <label className="block text-sm font-medium text-gray-800">Generative engine — model tiers</label>
            <p className="mt-0.5 mb-3 text-xs text-gray-500">
              Tune the intelligence of the engine that runs THIS network — the Lead (which
              agents handle each request), the Reducer judge (grades each output), and the
              Final synthesis (composes the answer). Tiers map to models:{' '}
              <span className="font-medium">Fast = {tierModel.fast}</span>,{' '}
              <span className="font-medium">Think = {tierModel.think}</span>,{' '}
              <span className="font-medium">Reason = {tierModel.reason}</span>. Takes effect
              on the next released version (frozen per version, like agent config).
            </p>
            {[
              // All three default to Fast (Haiku) so a new network runs cheap; upgrade any
              // time. The default MUST match the engine's runtime fallback (all 'fast').
              { key: 'engineLeadTier', label: 'Lead / router', defTier: 'fast',
                hint: `Picks which agents handle each request. Fast (${tierModel.fast}) is usually enough.` },
              { key: 'engineJudgeTier', label: 'Reducer judge', defTier: 'fast',
                hint: `Grades each agent’s output against your intent. Upgrade to Think (${tierModel.think}) for more rigorous grading.` },
              { key: 'engineFinalTier', label: 'Final synthesis', defTier: 'fast',
                hint: `Composes the final answer from all contributions. Upgrade to Reason (${tierModel.reason}) for highest-quality synthesis.` },
            ].map((row) => (
              <div key={row.key} className="mb-3 flex items-center justify-between gap-3">
                <span className="min-w-0">
                  <span className="block text-sm text-gray-700">{row.label}</span>
                  <span className="block text-xs text-gray-500">{row.hint}</span>
                </span>
                <select
                  value={formData?.[row.key] || ''}
                  onChange={(e) => setFormData((d) => ({ ...(d || {}), [row.key]: e.target.value }))}
                  data-testid={`engine-tier-${row.key}`}
                  className="shrink-0 rounded-md border border-gray-300 px-2 py-1 text-sm"
                >
                  <option value="">Default ({tierModel[row.defTier]})</option>
                  <option value="fast">Fast — {tierModel.fast}</option>
                  <option value="think">Think — {tierModel.think}</option>
                  <option value="reason">Reason — {tierModel.reason}</option>
                </select>
              </div>
            ))}
            <div className="mt-2 flex items-center justify-between gap-3 border-t border-gray-50 pt-3">
              <span className="min-w-0">
                <span className="block text-sm text-gray-700">Reducer judge</span>
                <span className="block text-xs text-gray-500">
                  Off = trust each agent’s self-score and skip the extra quality check (faster, cheaper).
                </span>
              </span>
              <select
                value={formData?.engineJudgeMode || ''}
                onChange={(e) => setFormData((d) => ({ ...(d || {}), engineJudgeMode: e.target.value }))}
                data-testid="engine-judge-mode"
                className="shrink-0 rounded-md border border-gray-300 px-2 py-1 text-sm"
              >
                <option value="">Default (on)</option>
                <option value="on">On</option>
                <option value="off">Off</option>
              </select>
            </div>
            {/* REDUCER-ON-CLAUDE-CODE (CLAUDE-CODE-MIGRATION.md) — do the reducers reason on
                claude-code (a workspace-reading agent that decomposes epics into cohesive subtasks +
                synthesizes) vs a single fast call? Frozen with the release. f.synth defaults ON (the
                task-graph author); n.synth (per-node judge) defaults OFF (costs a microVM per node). */}
            <div className="mt-2 flex items-center justify-between gap-3 border-t border-gray-50 pt-3">
              <span className="min-w-0">
                <span className="block text-sm text-gray-700">Deep final synthesis (f.synth)</span>
                <span className="block text-xs text-gray-500">
                  Final synthesis + task-graph authoring/decomposition run as a workspace-reading agent.
                  Recommended on — it breaks epics into cohesive subtasks. Off = a single fast call.
                </span>
              </span>
              <input
                type="checkbox"
                checked={formData?.finalReducerClaudeCode === true}
                onChange={(e) => update('finalReducerClaudeCode', e.target.checked)}
                data-testid="final-reducer-claude-code"
                className="h-4 w-4 shrink-0 rounded border-gray-300"
              />
            </div>
            <div className="mt-2 flex items-center justify-between gap-3 border-t border-gray-50 pt-3">
              <span className="min-w-0">
                <span className="block text-sm text-gray-700">Deep per-node judge (n.synth)</span>
                <span className="block text-xs text-gray-500">
                  The per-node judge reasons as a workspace-reading agent. Powerful but costs more per
                  node — leave off unless this is a large network.
                </span>
              </span>
              <input
                type="checkbox"
                checked={formData?.nodeReducerClaudeCode === true}
                onChange={(e) => update('nodeReducerClaudeCode', e.target.checked)}
                data-testid="node-reducer-claude-code"
                className="h-4 w-4 shrink-0 rounded border-gray-300"
              />
            </div>
          </div>
          )}

          {/* Shared tools + default workspace for the WHOLE room (doc/arch/ASSETS.md).
              Every agent in the room inherits these on top of its own tools (union)
              and as its workspace fallback — resolved in the single runtime
              chokepoint, frozen with the release, room-override-able. Same picker
              the agent Tools tab uses; agentId is the reserved room key. */}
          <div className="mt-5 border-t border-gray-100 pt-4">
            <label className="block text-sm font-medium text-gray-800">Shared tools &amp; workspace</label>
            <p className="mt-0.5 mb-2 text-xs text-gray-500">
              Tools, MCP connections, and a default workspace every agent in the
              room inherits. Agents keep their own tools too — these are added on
              top and shown as <span className="font-medium">Inherited</span>. The
              workspace is a fallback when an agent has none of its own.
            </p>
            <AgentToolPicker
              value={Array.isArray(formData.tools) ? formData.tools : []}
              onChange={(v) => update('tools', v)}
              tools={toolsCatalog.tools}
              loading={toolsCatalog.loading}
              error={toolsCatalog.error}
              networkId={networkId}
              agentId={ROOM_CONTEXT_AGENT_KEY}
              env={TOOLS_ENV}
              tenantId={tenantId}
              mcpCatalog={mcpCatalog.mcps}
              mcpValue={Array.isArray(formData.enabledMcpIds) ? formData.enabledMcpIds : []}
              onMcpChange={(v) => update('enabledMcpIds', v)}
              mcpInstancesLoader={async () => {
                const { mcpCredentials } = await listMcpCredentials({
                  networkId: networkId || undefined,
                });
                const byId = {};
                for (const r of mcpCredentials || []) {
                  (byId[r.mcpId] = byId[r.mcpId] || []).push({
                    instanceId: r.instanceId || 'default',
                    label: r.label || r.displayName || r.mcpId,
                    scope: r.scope,
                  });
                }
                return byId;
              }}
              workspaceCatalog={workspaceCatalog}
              workspaceValue={formData.defaultWorkspaceId || ''}
              onWorkspaceChange={(v) => update('defaultWorkspaceId', v)}
              // REPLACE-ONLY (CORE-AGENTS.md §4.4): the network's shared workspace is a
              // defaulted primitive — the user can swap it but can't remove it (× hidden).
              // Collaboration is mandatory; a network always has a workspace.
              workspaceRemovable={false}
              connectorInstancesLoader={async () => {
                const r = await listSecretsRegistry({ networkId: networkId || undefined });
                const byTool = {};
                for (const row of (r?.secrets || [])) {
                  const tn = String(row?.toolName || '').trim();
                  if (!tn || tn === 'workspace' || tn === 'skill') continue;
                  (byTool[tn] = byTool[tn] || []).push({
                    secretId: row.secretId,
                    secretName: row.secretName,
                    testStatus: row.testStatus,
                    scope: row.scope,
                  });
                }
                return byTool;
              }}
              onNavigateToConnections={onNavigateToConnections}
              // ROUND-TRIP AUTO-ASSIGN (parity with the per-agent picker below): when the
              // user creates a workspace / connector / MCP in Connections from THIS Shared
              // picker, the parent hands the new item back via ``pendingToolAssign`` and the
              // picker auto-assigns it (workspace → onWorkspaceChange, etc.). Without these
              // two props the Shared row silently dropped the return trip — the user had to
              // re-open "Add a tool" and pick the just-created item by hand.
              pendingToolAssign={pendingToolAssign}
              onPendingToolAssignConsumed={onPendingToolAssignConsumed}
            />
          </div>

          {/* VERBOSE — cost telemetry in the run block. OFF by default (the mirror image of
              Economy mode, which is ON by default): the per-agent model/token/$ chips and the
              "All Leads (routing)" / "All Reducers (judging)" breakdown are DEVELOPER telemetry —
              useful while tuning a network, noise for someone reading an answer. The headline
              "Total run cost" always shows in the run header regardless, so cost is never hidden
              entirely. Frozen with the release like the other Shared settings. */}
          <div className="mt-5 border-t border-gray-100 pt-4">
            <label className="flex cursor-pointer items-start gap-3">
              <input
                type="checkbox"
                checked={formData.verbose === true}
                onChange={(e) => update('verbose', e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                data-testid="shared-verbose-mode"
              />
              <span className="min-w-0">
                <span className="block text-sm font-medium text-gray-800">Verbose (cost details)</span>
                <span className="mt-0.5 block text-xs text-gray-500">
                  Off by default. When on, each run shows the full cost breakdown — model, tokens
                  and $ per agent, plus routing and judging subtotals. The total run cost is always
                  shown in the run header either way. Takes effect on the next released version.
                </span>
              </span>
            </label>
          </div>

          {/* GET REASONING (debug) — enables extended-thinking on the DECISION-MAKER LLM calls
              (each manager's route/handle/done decision + the non-economy reducer judge) so the
              user can see WHY a choice was made, in the thinking drawer. Costs output tokens →
              a debug aid, OFF by default. Frozen with the release like the other Shared settings. */}
          <div className="mt-5 border-t border-gray-100 pt-4">
            <label className="flex cursor-pointer items-start gap-3">
              <input
                type="checkbox"
                checked={formData.getReasoning === true}
                onChange={(e) => update('getReasoning', e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                data-testid="shared-get-reasoning"
              />
              <span className="min-w-0">
                <span className="block text-sm font-medium text-gray-800">Get Reasoning (debug)</span>
                <span className="mt-0.5 block text-xs text-gray-500">
                  Off by default. When on, the manager's decision (route / handle / answer) and the
                  reducer's judgment show their chain-of-thought in the thinking drawer — for
                  debugging why a choice was made. Adds LLM output cost, so use it while diagnosing,
                  not permanently. Takes effect on the next released version.
                </span>
              </span>
            </label>
          </div>
          </div>

          {/* Action bar — pinned to the bottom, same as the per-agent form. */}
          <div className="flex-shrink-0 flex justify-end px-6 py-4 border-t border-gray-200 bg-white">
            <button
              type="button"
              onClick={handleSave}
              disabled={!isDirty || saving}
              data-testid="room-context-save"
              className="px-2.5 py-1 text-xs font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {saving ? (
                <>
                  <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        </fieldset>
      )}

      {/* Detail: Tabbed form. We wrap in a ``<fieldset disabled>`` when a peer
          holds the lock so every nested input is non-interactive without
          each input having to opt in individually. The fieldset is
          invisible to screen readers when not disabled. */}
      {selectedKey !== ROOM_CONTEXT_AGENT_KEY && formData && (
        <>
        <fieldset
          disabled={isPeerHolder}
          className={`flex-1 min-h-0 flex flex-col overflow-hidden ${
            isPeerHolder ? 'opacity-60' : ''
          }`}
        >
          <Tab.Group>
            <Tab.List className="flex-shrink-0 flex gap-2 px-6 py-3 bg-gray-50 border-b border-gray-200 overflow-x-auto">
              {['Identity', 'Brain & Memory', 'Safety & Approvals', 'Tools & Routing'].map((label) => (
                <Tab
                  key={label}
                  className={({ selected }) =>
                    `px-3 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
                      selected ? 'bg-white shadow-sm text-blue-600 border border-gray-200' : 'text-gray-600 hover:text-gray-900'
                    }`
                  }
                >
                  {label}
                </Tab>
              ))}
            </Tab.List>
            <Tab.Panels className="flex-1 min-h-0 overflow-y-auto p-6">
              {/* Tab 1: Persona & Identity */}
              <Tab.Panel>
                <div className="space-y-4 max-w-2xl">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Agent ID (read-only)</label>
                    <input
                      type="text"
                      value={formData.logicalId}
                      readOnly
                      className="block w-full rounded-lg border border-gray-200 bg-gray-100 px-3 py-2 text-sm text-gray-600"
                    />
                    <p className="mt-1 text-xs text-gray-500">The agent's unique name in this network. Set at creation and can't be changed.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email address</label>
                    <input
                      type="text"
                      value={formData.identity?.email_prefix || ''}
                      onChange={(e) => update('identity.email_prefix', e.target.value)}
                      className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    />
                    <p className="mt-1 text-xs text-gray-500">The name part of the agent's email address (the bit before the @).</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email signature</label>
                    <textarea
                      value={formData.identity?.signature || ''}
                      onChange={(e) => update('identity.signature', e.target.value)}
                      rows={3}
                      className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    />
                    <p className="mt-1 text-xs text-gray-500">Added to the bottom of emails this agent sends.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Instructions</label>
                    <textarea
                      value={formData.systemPrompt || ''}
                      onChange={(e) => update('systemPrompt', e.target.value)}
                      rows={14}
                      className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm font-mono focus:border-blue-500 focus:ring-1 focus:ring-blue-500 min-h-[280px]"
                    />
                    <p className="mt-1 text-xs text-gray-500">Tell the agent who it is, what it does, and how it should behave.</p>
                  </div>
                </div>
              </Tab.Panel>

              {/* Tab 2: Brain & Memory */}
              <Tab.Panel>
                <div className="space-y-4 max-w-xl">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Thinking power</label>
                    <select
                      value={formData.modelId || 'think'}
                      onChange={(e) => update('modelId', e.target.value)}
                      className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    >
                      {MODEL_OPTIONS.map((m) => (
                        <option key={m} value={m}>{MODEL_LABELS[m] || m}</option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-gray-500">How much reasoning this agent needs. Higher tiers are smarter but cost more — most agents work well on Fast.</p>
                    {genericModelSuggestion(formData.modelRecommendation) ? (
                      <p className="mt-1 text-xs text-indigo-600">
                        Architect’s suggestion: {genericModelSuggestion(formData.modelRecommendation)}.
                        Every agent defaults to Fast to keep costs low — upgrade here if this
                        role needs it.
                      </p>
                    ) : null}
                  </div>
                  {SHOW_BEDROCK_KNOWLEDGE_BASE_UI && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Knowledge Base ID</label>
                      <input
                        type="text"
                        value={formData.knowledgeBaseId || ''}
                        onChange={(e) => update('knowledgeBaseId', e.target.value)}
                        placeholder="AWS Bedrock Knowledge Base ID"
                        className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                      />
                    </div>
                  )}
                  {(() => {
                    // Granular memory subsystems. The master toggle reflects
                    // "is any VISIBLE subsystem on"; flipping it sets all
                    // visible flags at once (hidden flags like knowledgeBase
                    // are forced OFF) and keeps the legacy `memoryEnabled`
                    // bool in sync for backwards-compat with old configs.
                    const memFlags = resolveMemoryFlags(formData);
                    const masterOn = VISIBLE_MEMORY_FLAG_KEYS.some((k) => memFlags[k]);
                    const setSub = (key, val) => {
                      update(`memory.${key}`, val);
                      const after = { ...memFlags, [key]: val };
                      // Cascade: turning a parent OFF forces its dependents off
                      // (e.g. vectorRecall off → autoVectorize off), so we never
                      // persist a write-only-but-unreadable combination.
                      if (!val) {
                        VISIBLE_MEMORY_FLAG_DEFS
                          .filter((d) => d.dependsOn === key)
                          .forEach((d) => {
                            update(`memory.${d.key}`, false);
                            after[d.key] = false;
                          });
                      }
                      // If turning the last one off, mirror legacy off; any on → legacy on.
                      update('memoryEnabled', VISIBLE_MEMORY_FLAG_KEYS.some((k) => after[k]));
                    };
                    const setMaster = (val) => {
                      VISIBLE_MEMORY_FLAG_KEYS.forEach((k) => update(`memory.${k}`, val));
                      // Hidden subsystems (KB while unused) are always persisted OFF.
                      HIDDEN_MEMORY_FLAG_KEYS.forEach((k) => update(`memory.${k}`, false));
                      // Always-on subsystems (org context) stay ON regardless —
                      // removable only by deleting the Data Plane file.
                      ALWAYS_ON_MEMORY_FLAG_KEYS.forEach((k) => update(`memory.${k}`, true));
                      update('memoryEnabled', val);
                    };
                    const renderFlagRow = (d) => {
                      // A dependent (e.g. autoVectorize → vectorRecall) is
                      // locked off + greyed when its parent is off.
                      const parentOff = d.dependsOn && !memFlags[d.dependsOn];
                      const checked = !parentOff && !!memFlags[d.key];
                      return (
                        <div
                          key={d.key}
                          className={`flex items-start gap-3 ${d.dependsOn ? 'ml-6' : ''} ${parentOff ? 'opacity-50' : ''}`}
                        >
                          <input
                            type="checkbox"
                            id={`mem-${d.key}`}
                            checked={checked}
                            disabled={!!parentOff}
                            onChange={(e) => setSub(d.key, e.target.checked)}
                            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 disabled:cursor-not-allowed"
                          />
                          <label htmlFor={`mem-${d.key}`} className={parentOff ? 'cursor-not-allowed' : 'cursor-pointer'}>
                            <span className="block text-sm font-medium text-gray-700">{d.label}</span>
                            <span className="block text-xs text-gray-500">{d.desc}</span>
                          </label>
                        </div>
                      );
                    };
                    return (
                      <div className="rounded-lg border border-gray-200 p-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <label htmlFor="memoryMaster" className="text-sm font-semibold text-gray-800">Memory</label>
                            <p className="text-xs text-gray-500">Master switch — turn off to make this agent forget everything below.</p>
                          </div>
                          <input
                            type="checkbox"
                            id="memoryMaster"
                            checked={masterOn}
                            onChange={(e) => setMaster(e.target.checked)}
                            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                        </div>
                        {masterOn && (
                          <div className="mt-3 space-y-4 border-t border-gray-100 pt-3">
                            {MEMORY_GROUPS.map((g) => {
                              const rows = VISIBLE_MEMORY_FLAG_DEFS.filter((d) => d.group === g.id);
                              if (rows.length === 0) return null;
                              return (
                                <div key={g.id} className="space-y-2">
                                  <div>
                                    <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-400">{g.label}</h4>
                                    <p className="text-xs text-gray-400">{g.hint}</p>
                                  </div>
                                  {rows.map(renderFlagRow)}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  <div className="border-t border-gray-100 pt-4">
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">Advanced tuning</h4>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Messages to remember before summarizing</label>
                    <input
                      type="number"
                      min={1}
                      value={formData.bufferSize ?? 5}
                      onChange={(e) => update('bufferSize', parseInt(e.target.value, 10) || 5)}
                      className="block w-full max-w-[120px] rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    />
                    <p className="mt-1 text-xs text-gray-500">How many recent messages to keep word-for-word before older ones are condensed into a summary. Default 5.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Max tool steps per turn</label>
                    <input
                      type="number"
                      min={1}
                      max={40}
                      value={formData.maxToolIterations ?? 12}
                      onChange={(e) => update('maxToolIterations', Math.min(40, Math.max(1, parseInt(e.target.value, 10) || 12)))}
                      className="block w-full max-w-[120px] rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    />
                    <p className="mt-1 text-xs text-gray-500">How many tools the agent may use while working on a single request. Default 12.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Max memory look-ups per turn</label>
                    <input
                      type="number"
                      min={1}
                      max={10}
                      value={formData.memoryRecallMaxIterations ?? 3}
                      onChange={(e) => update('memoryRecallMaxIterations', Math.min(10, Math.max(1, parseInt(e.target.value, 10) || 3)))}
                      className="block w-full max-w-[120px] rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    />
                    <p className="mt-1 text-xs text-gray-500">How many times the agent may search its long-term memory while answering one request. Default 3.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Summary length limit</label>
                    <input
                      type="number"
                      min={100}
                      value={formData.maxSummaryLength ?? 50000}
                      onChange={(e) => update('maxSummaryLength', parseInt(e.target.value, 10) || 50000)}
                      className="block w-full max-w-[160px] rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    />
                    <p className="mt-1 text-xs text-gray-500">Maximum size (in characters) of the condensed conversation summary. Default 50,000.</p>
                  </div>
                </div>
              </Tab.Panel>

              {/* Tab 3: Safety & Permissions */}
              <Tab.Panel>
                <div className="space-y-4 max-w-xl">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Acting on its own</label>
                    <select
                      data-testid="agent-acting-mode"
                      value={EMAIL_MODE_OPTIONS.includes(String(formData.permissions?.email_mode || '').toUpperCase())
                        ? String(formData.permissions.email_mode).toUpperCase() : (formData.permissions?.email_mode ? 'SUPERVISED' : EMAIL_MODE_UNSET)}
                      onChange={(e) => update('permissions.email_mode', e.target.value)}
                      className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    >
                      {EMAIL_MODE_OPTIONS.map((m) => (
                        <option key={m} value={m}>{EMAIL_MODE_LABELS[m] || m}</option>
                      ))}
                    </select>
                    <p className="mt-1 text-xs text-gray-500">Applies to its emails and its answers in rooms: act directly, ask the Approver only when it&apos;s below the confidence needed, or always draft for the Approver. In a room, a waiting answer shows as pending the Approver&apos;s confirmation.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Confidence needed to act</label>
                    <input
                      type="number"
                      min={0}
                      max={1}
                      step={0.01}
                      data-testid="agent-confidence-needed"
                      value={formData.permissions?.confidence_threshold ?? CONFIDENCE_UNSET}
                      onChange={(e) => update('permissions.confidence_threshold', parseFloat(e.target.value) || 0.95)}
                      className="block w-full max-w-[120px] rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    />
                    <p className="mt-1 text-xs text-gray-500">How sure the agent must be (0–1) to act on its own when it&apos;s set to &quot;Ask the Approver when unsure&quot;. Below this, its email or room answer waits for the Approver. New agents start at 0.95; an agent with none saved uses 0.7.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Approver</label>
                    <SupervisorMemberPicker
                      tenantId={tenantId}
                      value={formData.permissions?.supervisor_email || ''}
                      onChange={(email) => update('permissions.supervisor_email', email)}
                    />
                    <p className="mt-1 text-xs text-gray-500">Who reviews this agent's drafts, its waiting room answers and its approval requests (a room's owner can set a different Approver for that room). An Approver can also let this agent stand in for them (Profile → Stand-in).</p>
                  </div>
                  {/*
                    HitL SLA — per-agent claim/resolve budget for approvals
                    this agent raises. When the budget elapses without a
                    decision, the backend sweeper auto-escalates the approval
                    lease to workspace admins (the Overwatch page). We expose
                    a value + unit pair rather than raw seconds so supervisors
                    can think in human terms (e.g. "4 hours" for Legal review,
                    "15 minutes" for Shipping).
                  */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Approval wait time
                      <span className="ml-2 text-xs font-normal text-gray-500">
                        if no one responds in time, the request goes to workspace admins
                      </span>
                    </label>
                    <ApprovalTimeoutInput
                      minutes={
                        typeof formData.approvalTimeoutMinutes === 'number'
                          ? formData.approvalTimeoutMinutes
                          : DEFAULT_APPROVAL_TIMEOUT_MINUTES
                      }
                      onChange={(mins) => update('approvalTimeoutMinutes', mins)}
                    />
                  </div>
                  {formData.type === 'UNIVERSAL_BUILDER' ? (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Builder Execution Mode
                      </label>
                      <p className="mb-2 text-xs text-gray-500">
                        Autonomous: Claude Code auto-approves all tool use (CI-style).
                        Interactive: pauses at each tool call for human approval from the room.
                      </p>
                      <select
                        value={formData.builderExecutionMode === 'interactive' ? 'interactive' : 'background'}
                        onChange={(e) => update('builderExecutionMode', e.target.value)}
                        className="block w-full max-w-md rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                      >
                        <option value="background">Autonomous</option>
                        <option value="interactive">Interactive</option>
                      </select>
                    </div>
                  ) : null}
                  {formData.type === 'UNIVERSAL_BUILDER' ? (
                    <SafetyCapsRow formData={formData} update={update} />
                  ) : null}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Who it can consult</label>
                    <TagInput
                      value={formData.permissions?.allowed_consultants || ['*']}
                      onChange={(v) => update('permissions.allowed_consultants', v)}
                    />
                    <p className="mt-1 text-xs text-gray-500">Other agents this one may ask for help. Use <code>*</code> to allow everyone.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Who it can email</label>
                    <TagInput
                      value={formData.permissions?.allowed_emails || ['*']}
                      onChange={(v) => update('permissions.allowed_emails', v)}
                    />
                    <p className="mt-1 text-xs text-gray-500">Email addresses this agent is allowed to write to. Use <code>*</code> to allow anyone.</p>
                  </div>
                </div>
              </Tab.Panel>

              {/* Tab 4: Tools & Routing */}
              <Tab.Panel>
                <div className="space-y-4 max-w-xl">
                  <div>
                    {generative ? (
                      <>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Agent type</label>
                        <select
                          value={agentKindValue}
                          onChange={(e) => updateAgentKind(e.target.value)}
                          className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                          data-testid="agent-kind-select"
                        >
                          <option value="worker">Worker (fast — reasoning, tools, files)</option>
                          <option value="builder">Builder (repo + shell + CLI)</option>
                          <option value="dual">Dual (the Lead picks worker or builder per request)</option>
                        </select>
                        {agentKindValue === 'builder' ? (
                          <p className="mt-1 text-xs text-gray-500">
                            Runs in a real git workspace with shell + claude-code CLI. Heavier and
                            slower to start — use only for code/repo work. Takes effect on the next
                            released version.
                          </p>
                        ) : agentKindValue === 'dual' ? (
                          <p className="mt-1 text-xs text-gray-500">
                            The Lead runs this agent as a cheap worker by default and upgrades it to a
                            builder only when the request needs a built artifact. Takes effect on the
                            next released version.
                          </p>
                        ) : (
                          <p className="mt-1 text-xs text-gray-500">
                            A fast reasoning agent that reads/writes workspace files, calls MCP tools,
                            and uses skills — no repo/shell. Best for most work. Takes effect on the
                            next released version.
                          </p>
                        )}
                        {/* Architect's advisory recommendation — display only. */}
                        {formData.capabilityRecommendation ? (
                          <p className="mt-1 text-xs text-gray-400 italic">
                            Architect suggested: {formData.capabilityRecommendation}
                          </p>
                        ) : null}
                        {/* When the global Agent Mode (Shared) isn't 'Per agent', this per-agent
                            choice is overridden network-wide — tell the user so it's not a dead control. */}
                        {(() => {
                          const gm = String((agents?.[ROOM_CONTEXT_AGENT_KEY]?.agentMode) || 'dual').toLowerCase();
                          if (gm === 'agentconfig') return null;
                          const label = gm === 'worker' ? 'Worker' : gm === 'builder' ? 'Builder' : 'Dual';
                          return (
                            <p className="mt-1 text-xs text-amber-600">
                              Global Agent Mode is “{label}” (Shared) — it overrides this per-agent type.
                              Set Shared → “Per agent” to honor this.
                            </p>
                          );
                        })()}
                      </>
                    ) : (
                      <>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Agent type (read-only)</label>
                        <input
                          type="text"
                          value={formData.type || 'AI'}
                          readOnly
                          className="block w-full rounded-lg border border-gray-200 bg-gray-100 px-3 py-2 text-sm text-gray-600"
                        />
                      </>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Seniority level</label>
                    <input
                      type="number"
                      min={1}
                      value={formData.level ?? 2}
                      onChange={(e) => update('level', parseInt(e.target.value, 10) || 2)}
                      className="block w-full max-w-[80px] rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    />
                    <p className="mt-1 text-xs text-gray-500">1 for managers and routers, 2 for specialists who do the work.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Reports to</label>
                    <input
                      type="text"
                      value={formData.reports_to || ''}
                      onChange={(e) => update('reports_to', e.target.value)}
                      placeholder="e.g. management"
                      className="block w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    />
                    <p className="mt-1 text-xs text-gray-500">Which agent this one answers to. Leave blank if it reports to no one.</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tools</label>
                    <p className="mb-2 text-xs text-gray-500">
                      External services this agent can use — connectors and MCP tools.
                      Authenticated MCPs let you pick a configured connection.
                    </p>
                    {/* Read-only mirror of what this agent inherits from the room
                        context (edit on ⚙ Room context). Shown so the creator sees
                        the effective tool set; not editable per-agent. */}
                    {(inheritedRoomTools.length > 0 || inheritedRoomWorkspace) && (
                      <div
                        className="mb-2 rounded-md border border-gray-200 bg-gray-50 px-2.5 py-2"
                        data-testid="agent-inherited-room-tools"
                      >
                        <p className="mb-1 text-[11px] font-medium text-gray-500">
                          Inherited from ⚙ Shared
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {inheritedRoomTools.map((it) => (
                            <span
                              key={`${it.kind}:${it.label}`}
                              className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-0.5 text-xs text-gray-600"
                              title="Shared by the room — edit on ⚙ Shared"
                            >
                              {it.label}
                              <span className="rounded bg-indigo-50 px-1 text-[10px] font-medium text-indigo-600">
                                Inherited
                              </span>
                            </span>
                          ))}
                          {inheritedRoomWorkspace && (
                            <span
                              className="inline-flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-0.5 text-xs text-gray-600"
                              title="Default workspace shared by the room — edit on ⚙ Shared"
                            >
                              {inheritedRoomWorkspace}
                              <span className="rounded bg-indigo-50 px-1 text-[10px] font-medium text-indigo-600">
                                Workspace · Inherited
                              </span>
                            </span>
                          )}
                        </div>
                      </div>
                    )}
                    {/* ONE unified chip picker for both connector tools
                        (``tools``) and MCP instances (``enabledMcpIds``). MCP
                        support is opt-in via the mcp* props; the connector-only
                        mount in PoolSettingsDrawer omits them and is unaffected.
                        ``enabledMcpIds`` entries are {id, instanceId, label}. */}
                    <AgentToolPicker
                      value={Array.isArray(formData.tools) ? formData.tools : []}
                      onChange={(v) => update('tools', v)}
                      tools={toolsCatalog.tools}
                      loading={toolsCatalog.loading}
                      error={toolsCatalog.error}
                      networkId={networkId}
                      agentId={selectedKey}
                      env={TOOLS_ENV}
                      tenantId={tenantId}
                      mcpCatalog={mcpCatalog.mcps}
                      mcpValue={Array.isArray(formData.enabledMcpIds) ? formData.enabledMcpIds : []}
                      onMcpChange={(v) => update('enabledMcpIds', v)}
                      mcpInstancesLoader={async () => {
                        const { mcpCredentials } = await listMcpCredentials({
                          networkId: networkId || undefined,
                        });
                        const byId = {};
                        for (const r of mcpCredentials || []) {
                          (byId[r.mcpId] = byId[r.mcpId] || []).push({
                            instanceId: r.instanceId || 'default',
                            label: r.label || r.displayName || r.mcpId,
                            scope: r.scope,
                          });
                        }
                        return byId;
                      }}
                      workspaceCatalog={workspaceCatalog}
                      workspaceValue={formData.defaultWorkspaceId || ''}
                      // SHARED NETWORK WORKSPACE: when the Shared row has one assigned it's
                      // pinned room-wide and OVERRIDES any per-agent workspace at runtime, so
                      // offering a per-agent pick here would be a silent no-op. Pass
                      // ``onWorkspaceChange={null}`` to hide the per-agent workspace tile (the
                      // picker gates its workspace UI on this handler); the note below explains.
                      onWorkspaceChange={perAgentWorkspaceLocked ? null : ((v) => update('defaultWorkspaceId', v))}
                      connectorInstancesLoader={async () => {
                        // Saved connector connections visible at build time
                        // (tenant + this network), grouped by toolName for the
                        // type-tile → instance-list flow.
                        const r = await listSecretsRegistry({ networkId: networkId || undefined });
                        const byTool = {};
                        for (const row of (r?.secrets || [])) {
                          const tn = String(row?.toolName || '').trim();
                          // 'workspace' + 'skill' share this registry but are not
                          // connectors (each has its own surface) — never group
                          // them into the connector instance map.
                          if (!tn || tn === 'workspace' || tn === 'skill') continue;
                          (byTool[tn] = byTool[tn] || []).push({
                            secretId: row.secretId,
                            secretName: row.secretName,
                            testStatus: row.testStatus,
                            scope: row.scope,
                          });
                        }
                        return byTool;
                      }}
                      onNavigateToConnections={onNavigateToConnections}
                      pendingToolAssign={pendingToolAssign}
                      onPendingToolAssignConsumed={onPendingToolAssignConsumed}
                    />
                    {perAgentWorkspaceLocked ? (
                      <p className="mt-2 text-[11px] text-indigo-700">
                        A workspace is unique per agent — this network's shared workspace
                        {' '}(<span className="font-medium">{networkSharedWorkspace}</span>) applies to
                        every agent, so there's no separate per-agent workspace. Change it on
                        {' '}<span className="font-medium">⚙ Shared</span>.
                      </p>
                    ) : null}
                  </div>
                </div>
              </Tab.Panel>
            </Tab.Panels>
          </Tab.Group>

          {/* Action bar */}
          <div className="flex-shrink-0 flex justify-end px-6 py-4 border-t border-gray-200 bg-white">
            <button
              onClick={handleSave}
              disabled={!isDirty || saving}
              className="px-2.5 py-1 text-xs font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {saving ? (
                <>
                  <svg className="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        </fieldset>
        </>
      )}
      </div>

      {/* Right: React Flow topology (parity with Build tab graph); highlights the selected logical agent. */}
      <aside
        className="flex min-h-[220px] w-full shrink-0 flex-col border-t border-gray-200 bg-gradient-to-br from-gray-50 to-blue-50/40 lg:h-auto lg:min-h-0 lg:w-[min(44%,31rem)] lg:min-w-[280px] lg:max-w-[36rem] lg:border-l lg:border-t-0"
        aria-label="Network topology"
      >
        <div className="flex-shrink-0 border-b border-gray-200/90 bg-white/95 px-4 py-3 backdrop-blur-[2px]">
          <h3 className="text-sm font-semibold text-gray-900">Network topology</h3>
          <p className="mt-0.5 text-[11px] leading-snug text-gray-500">
            Same diagram as the Build tab. Click an agent to load its config — the selected
            agent is highlighted in amber.
          </p>
        </div>
        <div className="min-h-[200px] flex-1 lg:min-h-0">
          {generative ? (
            /* Generative manifest org-tree → the standalone left→right tree view.
               Kept SEPARATE from the ASL GraphVisualization so neither can break the other.
               ``highlightLogicalId`` amber-rings + frames the selected agent so the user knows
               which node they're editing (parity with the ASL renderer) — matters on deep trees. */
            <ManifestTreeFlow
              data={topologyGraph}
              highlightLogicalId={selectedKey}
              dimmedAgentIds={stageCapDimmedIds}
              disabledByAgentId={disabledByAgentId}
              onAgentClick={handleGraphAgentClick}
            />
          ) : (
            <GraphVisualization
              data={topologyGraph}
              highlightLogicalId={selectedKey}
              readOnly
              stickyResetKey={networkId}
              onAgentClick={handleGraphAgentClick}
            />
          )}
        </div>
      </aside>
    </div>
  );
}
